// ============================================
// DATA SERVICE - Abstraction layer over Dexie
// ============================================
// All data access goes through this service.
// When a backend is added, swap implementations here - not in every component.

import db from './db';
import { mirrorCompanyMerge } from './crm-cache-reconcile';
import {
    mergeProjectReadResultForDexie,
    mergeProjectSaveResultForDexieOwned,
} from './project-media-cache-merge';
import { devLog } from '../utils/dev-log';
import { getActiveTenantId } from './tenant-resolver.js';
import { isEvalAiSingleAttemptPolicyActive } from './eval-ai-single-attempt-policy';
import {
    hasCompleteGeneratedQuoteStructure,
    normalizeCompletedStages,
    projectHasCompletedQuote,
    quoteCompletionScopeFingerprint,
    QUOTE_COMPLETION_CONTRACT_VERSION,
} from '../utils/project-stage.js';
import {
    buildEmailSentProjectUpdate,
    buildPdfGeneratedProjectUpdate,
    buildPrintOpenedProjectUpdate,
} from '../utils/pdf-generation-audit.js';
import { sanitizeStage5ReadinessReview } from '../utils/stage5-readiness-review.js';
import { serverProjectContentSignature } from '../utils/project-fingerprint';
import {
    emitBackendReachability,
    isBackendReachable,
    probeQuoteMakerHealth,
    setBackendReachable,
    subscribeBackendReachability,
} from './network-resilience';
import {
    LIFECYCLE_RECEIPTS_AT_FIELD,
    PROJECT_SUMMARY_JOB_FIELDS_SETTING,
    buildSummaryJobFieldsSnapshot,
    resolveOfflineSummaryRevisionState,
    stampCachedServerRow,
    withLifecycleReceipts,
} from './project-summary-job-fields';

/**
 * The Job fields and send state of the last online project summary, per quote
 * (see project-summary-job-fields.ts). An empty map when there is none or it
 * cannot be read: the offline summary then falls back to the cached rows.
 */
async function _readSummaryJobFieldsSnapshot() {
    try {
        const record = await db.app_settings.get(PROJECT_SUMMARY_JOB_FIELDS_SETTING);
        const rows = record?.value?.rows;
        return rows && typeof rows === 'object' && !Array.isArray(rows) ? rows : {};
    } catch {
        return {};
    }
}

/**
 * Keep the Job fields and send state of a successful online summary for the
 * offline one.
 * Writes only when they changed, and never while the quote evaluator's
 * read-only policy is active (checked again right before the write, because
 * this runs detached from the read that triggered it).
 */
async function _rememberSummaryJobFields(rows) {
    const next = buildSummaryJobFieldsSnapshot(rows);
    const current = await _readSummaryJobFieldsSnapshot();
    if (JSON.stringify(current) === JSON.stringify(next)) return;
    if (isEvalAiSingleAttemptPolicyActive()) return;
    await db.app_settings.put({ key: PROJECT_SUMMARY_JOB_FIELDS_SETTING, value: { rows: next } });
}

// ============================================
// QUOTES
// ============================================
const quotes = {
    async getAll() {
        return db.quotes.orderBy('created_at').reverse().toArray();
    },
    async save(quote) {
        return db.quotes.put(quote);
    },
    async delete(id) {
        return db.quotes.delete(id);
    }
};

// ============================================
// DRAFTS
// ============================================
const drafts = {
    async get() {
        return db.drafts.get('current');
    },
    async save(draft) {
        return db.drafts.put({ ...draft, id: 'current', updated_at: new Date().toISOString() });
    },
    async clear() {
        return db.drafts.delete('current');
    }
};

// ============================================
// PROJECTS
// ============================================
const projects = {
    async getAll() {
        return db.projects.orderBy('updated_at').reverse().toArray();
    },
    async getById(id) {
        return db.projects.get(id);
    },
    async getVersion(id) {
        const row = await db.projects.get(id);
        if (!row) return null;
        return {
            tenantId: getActiveTenantId(),
            projectId: row.id,
            updatedAt: row.updatedAt || row.updated_at || null,
        };
    },
    async save(project) {
        // Dexie's 'projects' store declares keyPath: 'id' - a put() with no
        // id field throws the cryptic "Evaluating the object store's key path
        // did not yield a value" error. Callers sometimes pass partial deltas
        // like saveProject({ wms: ... }); guard here so Dexie stays in sync
        // for full records and we emit a loud signal when a caller slipped
        // through without an id (rather than silent cache drift).
        if (!project || !project.id) {
            console.warn('[QM] projects.save called without an id - Dexie put skipped. Payload keys:', project ? Object.keys(project) : '(nullish)');
            return null;
        }
        const looksPartial = !('title' in project) && !('formData' in project) && !('scope' in project) && !('currentStage' in project);
        if (looksPartial) {
            const existing = await db.projects.get(project.id);
            if (existing) {
                return db.projects.put({
                    ...existing,
                    ...project,
                    updated_at: project.updated_at || new Date().toISOString(),
                });
            }
            console.warn('[QM] projects.save received a partial patch for a missing project - Dexie put skipped. Payload keys:', Object.keys(project));
            return null;
        }
        return db.projects.put(project);
    },
    async duplicate(_sourceProjectId, targetProject, serverResult = null) {
        // A server result is the new quote's whole row, so it versions the
        // cached Job columns (project-summary-job-fields.ts).
        const row = serverResult && typeof serverResult === 'object'
            ? stampCachedServerRow(await mergeProjectSaveResultForDexieOwned(targetProject, serverResult), serverResult)
            : targetProject;
        if (!row?.id) throw new TypeError('projects.duplicate requires a target project id');
        await db.projects.put(row);
        return row;
    },
    async delete(id) {
        return db.projects.delete(id);
    },
    async updateQuoteStatus(id, quoteStatus, receipt = {}) {
        const now = new Date().toISOString();
        // The receipt time (the server's updatedAt when the proxy has it) lets
        // the offline summary keep this status over an older online list.
        return db.projects.update(id, {
            quoteStatus,
            updated_at: now,
            [`${LIFECYCLE_RECEIPTS_AT_FIELD}.quoteStatus`]: receipt?.updatedAt || now,
        });
    },
    async recordPdfGenerated(id, detail = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) {
                const error = new Error(`Project ${id} is not available in the local cache.`);
                error.code = 'PROJECT_NOT_LOCAL';
                throw error;
            }
            const generatedAt = detail.pdfGeneratedAt || detail.generatedAt || new Date().toISOString();
            const updatedAt = detail.updatedAt || generatedAt;
            const updated = withLifecycleReceipts(
                buildPdfGeneratedProjectUpdate(current, detail, generatedAt, updatedAt),
                ['pdf'],
                updatedAt,
            );
            await db.projects.put(updated);
            return updated;
        });
    },
    async recordPrintOpened(id, detail = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) {
                const error = new Error(`Project ${id} is not available in the local cache.`);
                error.code = 'PROJECT_NOT_LOCAL';
                throw error;
            }
            const openedAt = detail.printOpenedAt || detail.openedAt || new Date().toISOString();
            const updatedAt = detail.updatedAt || openedAt;
            const built = buildPrintOpenedProjectUpdate(current, openedAt, updatedAt);
            const updated = {
                ...built,
                aiContext: { ...(current.aiContext || {}), ...(detail.aiContext || {}), ...(built.aiContext || {}) },
            };
            await db.projects.put(updated);
            return updated;
        });
    },
    async recordEmailSent(id, detail = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) {
                const error = new Error(`Project ${id} is not available in the local cache.`);
                error.code = 'PROJECT_NOT_LOCAL';
                throw error;
            }
            const sentAt = detail.emailSentAt || detail.sentAt || new Date().toISOString();
            const updatedAt = detail.updatedAt || sentAt;
            const built = buildEmailSentProjectUpdate(current, detail.finalSentScope, sentAt, updatedAt);
            const updated = withLifecycleReceipts({
                ...built,
                aiContext: { ...(current.aiContext || {}), ...(detail.aiContext || {}), ...(built.aiContext || {}) },
            }, ['email'], updatedAt);
            await db.projects.put(updated);
            return updated;
        });
    },
    async recordCostingsSent(id, detail = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) throw Object.assign(new Error(`Project ${id} is not available in the local cache.`), { code: 'PROJECT_NOT_LOCAL' });
            const sentAt = detail.costingsSentAt || detail.sentAt || new Date().toISOString();
            const updatedAt = detail.updatedAt || sentAt;
            const serverContext = detail.aiContext && typeof detail.aiContext === 'object' ? detail.aiContext : {};
            const updated = {
                ...current,
                costingsSent: true,
                costingsSentAt: sentAt,
                costingsSentTo: detail.to || '',
                updatedAt,
                updated_at: updatedAt,
                aiContext: {
                    ...(current.aiContext || {}),
                    ...serverContext,
                    costingsSent: true,
                    costingsSentAt: sentAt,
                    costingsSentTo: detail.to || serverContext.costingsSentTo || '',
                },
            };
            await db.projects.put(updated);
            return updated;
        });
    },
    async recordOutcome(id, detail = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) throw Object.assign(new Error(`Project ${id} is not available in the local cache.`), { code: 'PROJECT_NOT_LOCAL' });
            const status = detail.status;
            if (!['won', 'lost'].includes(status)) throw new TypeError('Outcome status must be won or lost');
            const serverContext = detail.aiContext && typeof detail.aiContext === 'object' ? detail.aiContext : {};
            const sameOutcome = (current.quoteStatus || current.quote_status || current.status) === status;
            const existingEvent = status === 'won'
                ? (current.wonAt ?? current.aiContext?.wonAt ?? current.aiContext?.won_at)
                : (current.lostAt ?? current.aiContext?.lostAt ?? current.aiContext?.lost_at);
            const existingEventIsValid = typeof existingEvent === 'string'
                && !Number.isNaN(Date.parse(existingEvent));
            const outcomeAt = detail.outcomeAt
                || (sameOutcome && existingEventIsValid ? existingEvent : new Date().toISOString());
            // A repeated outcome keeps its historical event time but still
            // receives a fresh local write token unless the server supplied
            // its authoritative updatedAt receipt.
            const updatedAt = detail.updatedAt || new Date().toISOString();
            const existingLostReason = current.lostReason
                ?? current.aiContext?.lostReason
                ?? current.aiContext?.lost_reason
                ?? '';
            const outcomePatch = status === 'won'
                ? { wonAt: outcomeAt, lostAt: null, lostReason: null }
                : {
                    lostAt: outcomeAt,
                    wonAt: null,
                    lostReason: detail.reason ?? (sameOutcome ? existingLostReason : ''),
                };
            const updated = withLifecycleReceipts({
                ...current,
                status,
                quoteStatus: status,
                ...outcomePatch,
                updatedAt,
                updated_at: updatedAt,
                aiContext: { ...(current.aiContext || {}), ...serverContext, ...outcomePatch },
            }, ['status', 'quoteStatus'], updatedAt);
            await db.projects.put(updated);
            return updated;
        });
    },
    async recordFollowUp(id, detail = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) throw Object.assign(new Error(`Project ${id} is not available in the local cache.`), { code: 'PROJECT_NOT_LOCAL' });
            if (!detail.dueDate) throw new TypeError('A follow-up dueDate is required');
            const updatedAt = detail.updatedAt || new Date().toISOString();
            const serverContext = detail.aiContext && typeof detail.aiContext === 'object' ? detail.aiContext : {};
            const followUpPatch = { nextFollowUp: detail.dueDate, nextFollowUpNote: detail.note || '' };
            const updated = {
                ...current,
                ...followUpPatch,
                updatedAt,
                updated_at: updatedAt,
                aiContext: { ...(current.aiContext || {}), ...serverContext, ...followUpPatch },
            };
            await db.projects.put(updated);
            return updated;
        });
    },
    async setCrmLink(id, link = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) throw Object.assign(new Error(`Project ${id} is not available in the local cache.`), { code: 'PROJECT_NOT_LOCAL' });
            // Presence, not truthiness — null clears the link, absent leaves it.
            const patch = {};
            if ('companyId' in link) { patch.companyId = link.companyId ?? null; patch.company_id = link.companyId ?? null; }
            if ('propertyId' in link) { patch.propertyId = link.propertyId ?? null; patch.property_id = link.propertyId ?? null; }
            // A bookkeeping write does NOT advance the version. Carry the
            // server's unchanged updatedAt through when we have it, and
            // otherwise keep the cached one — minting `new Date()` here would
            // hand the tab a token PostgreSQL never issued, and its next full
            // save would then be refused as stale.
            const updatedAt = link.updatedAt || current.updatedAt || current.updated_at || null;
            const updated = { ...current, ...patch, updatedAt, updated_at: updatedAt };
            await db.projects.put(updated);
            return updated;
        });
    },
    // Client matching on open (SPEC 2026-10-08 D2, D3). The decision needs
    // every firm and contact in the business, so only the server makes it:
    // offline this throws and the job opens without the prompt. After an
    // online answer the proxy passes the server's response, and a link it
    // reports is mirrored into the cache with the cached save token kept
    // (a bookkeeping write). A job missing from the cache is left alone.
    async resolveCrmLink(id, _input, serverResult) {
        if (!serverResult || typeof serverResult !== 'object') {
            throw new Error('Client matching needs a connection to QuoteMaker.');
        }
        const companyId = serverResult.company?.id ?? serverResult.companyId ?? null;
        if ((serverResult.state === 'linked' || serverResult.state === 'auto-linked') && companyId) {
            await db.transaction('rw', db.projects, async () => {
                const current = await db.projects.get(id);
                if (!current) return;
                const patch = { companyId, company_id: companyId };
                // null is an answer (the job has no site on the server, for
                // example the link dropped it) and clears it. Only a missing
                // key keeps the cached site.
                const propertyId = serverResult.propertyId !== undefined ? serverResult.propertyId : serverResult.property_id;
                if (propertyId !== undefined) { patch.propertyId = propertyId ?? null; patch.property_id = propertyId ?? null; }
                const updatedAt = current.updatedAt || current.updated_at || null;
                await db.projects.put({ ...current, ...patch, updatedAt, updated_at: updatedAt });
            });
        }
        return serverResult;
    },
    // "Match later" is stored on the job on the server (SPEC 2026-10-08 D3).
    // Mirrors the server's receipt; never written to the cache alone.
    async deferCrmLink(id, email, serverResult) {
        if (!serverResult || typeof serverResult !== 'object') {
            throw new Error('Saving "Match later" needs a connection to QuoteMaker.');
        }
        await db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) return;
            const deferredAt = serverResult.crmMatchDeferredAt ?? serverResult.crm_match_deferred_at ?? new Date().toISOString();
            const deferredEmail = serverResult.crmMatchDeferredEmail ?? serverResult.crm_match_deferred_email
                ?? (typeof email === 'string' && email.trim() ? email.trim() : null);
            const updatedAt = serverResult.updatedAt || current.updatedAt || current.updated_at || null;
            await db.projects.put({
                ...current,
                crmMatchDeferredAt: deferredAt, crm_match_deferred_at: deferredAt,
                crmMatchDeferredEmail: deferredEmail, crm_match_deferred_email: deferredEmail,
                updatedAt, updated_at: updatedAt,
            });
        });
        return serverResult;
    },
    // A job's requesters (2026-10-13) live in Clients on the server
    // (project_contacts), not in the browser cache. Offline the read has no
    // answer and the caller shows the RFQ's requesters alone. After an online
    // write there is nothing to mirror: the links never ride the project row.
    async getRequesters() {
        throw new Error('Requesters need a connection to QuoteMaker.');
    },
    async setRequesters() {
        return null;
    },
    async completeQuote(id, {
        expectedUpdatedAt,
        readyAt,
        scopeFingerprint,
        readinessReview = null,
        serverResult = null,
    } = {}) {
        return db.transaction('rw', db.projects, async () => {
            const current = await db.projects.get(id);
            if (!current) {
                const error = new Error(`Project ${id} is not available in the local cache.`);
                error.code = 'PROJECT_NOT_LOCAL';
                throw error;
            }

            const actualUpdatedAt = current.updatedAt ?? current.updated_at ?? null;
            if (!expectedUpdatedAt
                || !actualUpdatedAt
                || Date.parse(actualUpdatedAt) !== Date.parse(expectedUpdatedAt)) {
                const error = new Error('The project changed after quote generation. Reload it before completing Stage 5.');
                error.code = 'PROJECT_WRITE_CONFLICT';
                error.projectId = id;
                error.expectedUpdatedAt = expectedUpdatedAt || null;
                error.actualUpdatedAt = actualUpdatedAt;
                throw error;
            }

            const actualFingerprint = quoteCompletionScopeFingerprint(current.scope);
            if (!scopeFingerprint || scopeFingerprint !== actualFingerprint) {
                const error = new Error('The quote completion proof no longer matches the current scope.');
                error.code = 'QUOTE_SCOPE_STALE';
                throw error;
            }
            if (!hasCompleteGeneratedQuoteStructure(current)) {
                const error = new Error('Stage 5 requires a generated quote bound to the current scope.');
                error.code = 'QUOTE_NOT_COMPLETE';
                throw error;
            }

            const effectiveReadyAt = serverResult?.readyForReviewAt || readyAt;
            const effectiveUpdatedAt = serverResult?.updatedAt || new Date().toISOString();
            const quoteData = current.quoteData ?? current.quote_data ?? {};
            const priorMeta = quoteData?.meta && typeof quoteData.meta === 'object'
                ? quoteData.meta
                : {};
            // The backend response is authoritative when present. An older
            // server that omits the field must not make Dexie claim a review
            // receipt PostgreSQL did not retain.
            const canonicalReadinessReview = sanitizeStage5ReadinessReview(
                serverResult ? (serverResult.readinessReview ?? null) : readinessReview,
            );
            const completionMeta = { ...priorMeta };
            delete completionMeta.readinessReview;
            if (canonicalReadinessReview) {
                completionMeta.readinessReview = canonicalReadinessReview;
            }
            const priorCompletion = priorMeta?.completion && typeof priorMeta.completion === 'object'
                ? priorMeta.completion
                : {};
            const nextQuoteData = {
                ...quoteData,
                meta: {
                    ...completionMeta,
                    completion: {
                        ...priorCompletion,
                        contractVersion: QUOTE_COMPLETION_CONTRACT_VERSION,
                        sourceScopeFingerprint: actualFingerprint,
                        readyForReviewAt: effectiveReadyAt,
                        // 2026-07-29: every quote that crosses this boundary was
                        // drafted by the pipeline, not written by the operator —
                        // markReadyForBuilderReview is the only production caller.
                        // The Run Sheet showed such a quote as "Ready to send",
                        // indistinguishable from one the operator built and
                        // checked himself, while the queue promised the opposite.
                        // The pair below is that promise made legible: set here,
                        // cleared by markQuoteReviewed when he saves at Stage 5.
                        aiDrafted: true,
                        reviewedAt: null,
                    },
                },
            };
            const completedStages = [...normalizeCompletedStages(
                current.completedStages ?? current.completed_stages,
            )];
            for (const stage of [1, 2, 3, 4, 5]) {
                if (!completedStages.includes(stage)) completedStages.push(stage);
            }
            completedStages.sort((a, b) => a - b);

            // quoteData.meta is the sole completion-review authority. Remove
            // the pre-terminal fallback copy so a later no-review run cannot
            // resurrect a legacy Stage 4 receipt through aiContext.
            const priorAiContext = current.aiContext ?? current.ai_context;
            const nextAiContext = priorAiContext && typeof priorAiContext === 'object' && !Array.isArray(priorAiContext)
                ? { ...priorAiContext }
                : {};
            delete nextAiContext.readinessReview;
            delete nextAiContext.readiness_review;

            const usesSnakeCaseQuote = current.quoteData === undefined && current.quote_data !== undefined;
            // The status is a send-state field the offline summary may take
            // from an older online list; the receipt time keeps this one.
            const next = withLifecycleReceipts({
                ...current,
                status: serverResult?.status || 'ready_for_builder_review',
                currentStage: serverResult?.currentStage ?? 5,
                current_stage: serverResult?.currentStage ?? 5,
                completedStages: serverResult?.completedStages || completedStages,
                completed_stages: serverResult?.completedStages || completedStages,
                readyForReviewAt: effectiveReadyAt,
                updatedAt: effectiveUpdatedAt,
                updated_at: effectiveUpdatedAt,
                needsReview: canonicalReadinessReview !== null,
                aiContext: nextAiContext,
                ...(usesSnakeCaseQuote ? { quote_data: nextQuoteData } : { quoteData: nextQuoteData }),
            }, ['status'], effectiveUpdatedAt);
            if (Object.prototype.hasOwnProperty.call(current, 'needs_review')) {
                next.needs_review = canonicalReadinessReview !== null;
            }
            if (Object.prototype.hasOwnProperty.call(current, 'ai_context')) {
                next.ai_context = nextAiContext;
            }
            await db.projects.put(next);
            return next;
        });
    },
    async getAllSummaries() {
        // Lightweight cursor - extracts summary fields without loading heavy binary data (photos/docs/videos)
        const summaries = [];
        // The Job columns (migration 120) and the send state a Revise resets
        // come from the cached row and from the last online summary's per-quote
        // snapshot: see project-summary-job-fields.ts for why both, and which
        // one wins.
        const jobSnapshot = await _readSummaryJobFieldsSnapshot();
        await db.projects.orderBy('updated_at').reverse().each(p => {
            const revision = resolveOfflineSummaryRevisionState(p, jobSnapshot[p.id]);
            summaries.push({
                id: p.id, title: p.title, address: p.address,
                created_at: p.created_at, updated_at: p.updated_at,
                currentStage: p.currentStage, completedStages: p.completedStages,
                status: p.status, quoteNumber: p.quoteNumber,
                // Outcome fields — getQuoteOutcome() reads quote_status /
                // wonAt / lostAt. The online summary carries quote_status;
                // without these the OFFLINE summary miscounts won/lost (and
                // the assistant's metrics tool can't compute win rate).
                quoteStatus: p.quoteStatus ?? p.quote_status ?? null,
                quote_status: p.quote_status ?? p.quoteStatus ?? null,
                wonAt: p.wonAt ?? p.won_at ?? null,
                lostAt: p.lostAt ?? p.lost_at ?? null,
                lostReason: p.lostReason ?? p.aiContext?.lostReason ?? null,
                costingsSent: p.costingsSent ?? p.aiContext?.costingsSent ?? false,
                costingsSentAt: p.costingsSentAt ?? p.aiContext?.costingsSentAt ?? null,
                costingsSentTo: p.costingsSentTo ?? p.aiContext?.costingsSentTo ?? '',
                nextFollowUp: p.nextFollowUp ?? p.aiContext?.nextFollowUp ?? null,
                nextFollowUpNote: p.nextFollowUpNote ?? p.aiContext?.nextFollowUpNote ?? '',
                // History-search haystack fields (buildProjectHaystack) — the
                // online summary carries qm_quote_no / project_type / intake
                // join columns, so the offline path must too or search+chips
                // silently degrade offline. Emit camelCase (frontend canon),
                // read either casing off the stored record.
                qmQuoteNo: p.qmQuoteNo ?? p.qm_quote_no ?? null,
                projectType: p.projectType ?? p.project_type ?? null,
                intakeJobId: p.intakeJobId ?? p.intake_job_id ?? null,
                intakeQuoteRequestNo: p.intakeQuoteRequestNo ?? p.intake_quote_request_no ?? null,
                // CRM grouping keys — without these the offline Dexie path can't
                // bucket quotes by company/site (the online API path carries them).
                // Emit snake_case (what the CRM consumers read), from either casing.
                company_id: p.company_id ?? p.companyId ?? null,
                property_id: p.property_id ?? p.propertyId ?? null,
                // Review label (migration 088). This summary is built FIELD BY
                // FIELD, so a new column on the online path is invisible
                // offline until it is named here — the Run Sheet's LABEL column
                // and its filter would silently go blank the moment the network
                // dropped. Emit camelCase (frontend canon), read either casing
                // off the stored record. Honest limit: this is the name/tone
                // last SYNCED onto the row; the definition list itself has no
                // Dexie mirror, so labels cannot be created or renamed offline.
                reviewLabelId: p.reviewLabelId ?? p.review_label_id ?? null,
                reviewLabelName: p.reviewLabelName ?? p.review_label_name ?? null,
                reviewLabelTone: p.reviewLabelTone ?? p.review_label_tone ?? 'slate',
                // Jobs (migration 120): quoteJobId, quoteRev, sourceQuoteRef,
                // sourceIssuer, quoteJobTitle, quoteJobQuoteRequestNo,
                // quoteJobStrataPlan, in camelCase like the online summary.
                // Without them, offline, a Rev 1 quote read as the Rev 0 that
                // went to the client, an imported quote lost its number, and
                // Job grouping and Job search stopped working.
                ...revision.jobFields,
                pdfGenerated: p.pdfGenerated, pdfGeneratedAt: p.pdfGeneratedAt,
                emailSent: p.emailSent, emailSentAt: p.emailSentAt,
                // When those Job columns come from the last online list, so
                // does the send state they were reported with (status,
                // quoteStatus, the PDF and email flags), except what this
                // device wrote after that list. Otherwise a revision the server
                // made would show the cached Rev 0's send state and read as
                // sent. Empty when the cached row is the current source.
                ...revision.lifecycle,
                kpiReport: p.kpiReport,
                projectFolder: p.projectFolder ? { parentName: p.projectFolder.parentName, subfolderName: p.projectFolder.subfolderName } : null,
                formData: p.formData,
                // v5.x: mirror the backend lean summary (backend/src/routes/projects.js)
                // — drop the full multi-MB quote_data blob (carry only the total)
                // and lean scope to title/amount-only, so the offline Dexie path
                // matches the online contract and avoids loading base64-laden blobs.
                quoteTotal: p.quoteData?.totals?.total ?? null,
                // The summary intentionally drops quoteData, so calculate the
                // structural completion proof while the full Dexie row is in
                // hand. Consumers must not infer this from Stage 5 alone.
                quoteComplete: projectHasCompletedQuote(p),
                // GST mode — value math (getQuoteValue / the assistant metrics
                // tool) reads this to decide ÷1.1. The online summary carries
                // scope_gst_inclusive; without it here, an offline GST-EXCLUSIVE
                // project would be wrongly divided by 1.1.
                scopeGstInclusive: p.scopeGstInclusive ?? p.scope_gst_inclusive ?? true,
                scope: Array.isArray(p.scope)
                    ? p.scope.map(it => ({ title: it?.title, description: it?.description, description_brief: it?.description_brief, code: it?.code, amount: it?.amount }))
                    : [],
                quoteNote: p.quoteNote, selectedHeroImage: p.selectedHeroImage,
                aiGeneratedItems: p.aiGeneratedItems, originalAiScope: p.originalAiScope,
                photoCount: p.photos?.length || 0,
                documentCount: p.documents?.length || 0,
                videoCount: p.videos?.length || 0,
            });
        });
        return summaries;
    },
    async cleanup() {
        // Strip video File objects that bloat storage - wrapped in transaction for atomicity
        let cleaned = 0;
        await db.transaction('rw', db.projects, async () => {
            const all = await db.projects.toArray();
            for (const project of all) {
                if (project.videos?.some(v => v.file || v.url)) {
                    await db.projects.update(project.id, {
                        videos: project.videos.map(v => ({ ...v, file: undefined, url: undefined })),
                    });
                    cleaned++;
                }
            }
        });
        if (cleaned > 0) console.log(`[DB] Cleaned ${cleaned} project(s)`);
        return cleaned;
    }
};

// ============================================
// BUSINESS PRICING
// ============================================
const businessPricing = {
    async get() {
        return db.business_pricing.get('default');
    },
    async save(data) {
        return db.business_pricing.put({ ...data, id: 'default', updated_at: new Date().toISOString() });
    }
};

// ============================================
// CLIENTS
// ============================================
const clients = {
    async getAll() {
        return db.clients.toArray();
    },
    async getById(id) {
        return db.clients.get(id);
    },
    async save(client) {
        return db.clients.put(client);
    },
    async delete(id) {
        return db.clients.delete(id);
    },
    async search(query) {
        if (!query) return db.clients.toArray();
        const q = query.toLowerCase();
        return db.clients.filter(c =>
            (c.company || '').toLowerCase().includes(q) ||
            (c.contactName || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q)
        ).toArray();
    }
};

// ============================================
// RECEIVABLES INTELLIGENCE
// ============================================
function getReceivableCaseStore() {
    if (!db?.receivable_cases) {
        throw new Error('receivable_cases store unavailable');
    }
    return db.receivable_cases;
}

function getReceivableStore(name) {
    if (!db?.[name]) {
        throw new Error(`${name} store unavailable`);
    }
    return db[name];
}

function localReceivableId(prefix) {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}_${crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const receivables = {
    async getAll() {
        return getReceivableCaseStore().toArray();
    },
    async count() {
        return getReceivableCaseStore().count();
    },
    async getById(id) {
        if (!id) return null;
        return getReceivableCaseStore().get(id);
    },
    async save(caseRecord) {
        if (!caseRecord?.id) {
            throw new Error('receivables.save requires an id');
        }
        const row = {
            ...caseRecord,
            updated_at: caseRecord.updated_at || new Date().toISOString(),
        };
        await getReceivableCaseStore().put(row);
        return row;
    },
    async bulkSave(cases = []) {
        const rows = Array.isArray(cases) ? cases : [];
        await getReceivableCaseStore().bulkPut(rows);
        return rows;
    },
    async update(id, patch = {}) {
        if (!id) return null;
        const store = getReceivableCaseStore();
        const existing = await store.get(id);
        if (!existing) return null;
        const next = {
            ...existing,
            ...patch,
            updated_at: patch.updated_at || new Date().toISOString(),
        };
        await store.put(next);
        return next;
    },
    async delete(id) {
        if (!id) return;
        return getReceivableCaseStore().delete(id);
    },
    async clear() {
        return getReceivableCaseStore().clear();
    },
};

// ============================================
// PROJECT ↔ LEARNING JUNCTION
// ============================================
// Receivables integration scaffolds live here until backend read-only bridges
// for Xero and email are deliberately added.
const receivableEvidence = {
    async getAll() {
        return getReceivableStore('receivable_evidence_items').toArray();
    },
    async count() {
        return getReceivableStore('receivable_evidence_items').count();
    },
    async getByCase(caseId) {
        if (!caseId) return [];
        return getReceivableStore('receivable_evidence_items').where('caseId').equals(caseId).sortBy('occurredAt');
    },
    async save(record) {
        const row = {
            ...record,
            id: record?.id || localReceivableId('rxe'),
            importedAt: record?.importedAt || new Date().toISOString(),
        };
        await getReceivableStore('receivable_evidence_items').put(row);
        return row;
    },
    async bulkSave(records = []) {
        const rows = (Array.isArray(records) ? records : []).map(record => ({
            ...record,
            id: record?.id || localReceivableId('rxe'),
            importedAt: record?.importedAt || new Date().toISOString(),
        }));
        await getReceivableStore('receivable_evidence_items').bulkPut(rows);
        return rows;
    },
    async deleteByCase(caseId) {
        if (!caseId) return 0;
        return getReceivableStore('receivable_evidence_items').where('caseId').equals(caseId).delete();
    },
    async clear() {
        return getReceivableStore('receivable_evidence_items').clear();
    },
};

const receivableProjectLinks = {
    async getAll() {
        return getReceivableStore('receivable_project_links').toArray();
    },
    async count() {
        return getReceivableStore('receivable_project_links').count();
    },
    async getByCase(caseId) {
        if (!caseId) return [];
        const rows = await getReceivableStore('receivable_project_links').where('caseId').equals(caseId).toArray();
        return rows.sort((a, b) => (b.score || 0) - (a.score || 0));
    },
    async save(record) {
        const row = {
            ...record,
            id: record?.id || `${record?.caseId || 'case'}:${record?.projectId || localReceivableId('project')}`,
            created_at: record?.created_at || new Date().toISOString(),
        };
        await getReceivableStore('receivable_project_links').put(row);
        return row;
    },
    async delete(id) {
        if (!id) return;
        return getReceivableStore('receivable_project_links').delete(id);
    },
    async clear() {
        return getReceivableStore('receivable_project_links').clear();
    },
};

const receivableActions = {
    async getAll() {
        return getReceivableStore('receivable_actions').toArray();
    },
    async count() {
        return getReceivableStore('receivable_actions').count();
    },
    async getByCase(caseId) {
        if (!caseId) return [];
        const rows = await getReceivableStore('receivable_actions').where('caseId').equals(caseId).toArray();
        return rows.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    },
    async save(record) {
        const row = {
            ...record,
            id: record?.id || localReceivableId('rxa'),
            status: record?.status || 'planned',
            created_at: record?.created_at || new Date().toISOString(),
        };
        await getReceivableStore('receivable_actions').put(row);
        return row;
    },
    async update(id, patch = {}) {
        if (!id) return null;
        const store = getReceivableStore('receivable_actions');
        const existing = await store.get(id);
        if (!existing) return null;
        const next = { ...existing, ...patch, updated_at: patch.updated_at || new Date().toISOString() };
        await store.put(next);
        return next;
    },
    async delete(id) {
        if (!id) return;
        return getReceivableStore('receivable_actions').delete(id);
    },
    async clear() {
        return getReceivableStore('receivable_actions').clear();
    },
};

const receivableSyncState = {
    async get(id) {
        if (!id) return null;
        return getReceivableStore('receivable_sync_state').get(id);
    },
    async getAll() {
        return getReceivableStore('receivable_sync_state').toArray();
    },
    async count() {
        return getReceivableStore('receivable_sync_state').count();
    },
    async save(record) {
        const row = {
            ...record,
            id: record?.id || [record?.provider || 'provider', record?.tenantId || 'tenant'].join(':'),
            updated_at: record?.updated_at || new Date().toISOString(),
        };
        await getReceivableStore('receivable_sync_state').put(row);
        return row;
    },
    async clear() {
        return getReceivableStore('receivable_sync_state').clear();
    },
};

// ============================================
// PIPELINE EXECUTIONS (Persisted AI call history)
// ============================================
const pipelines = {
    async save(execution) {
        return db.pipeline_executions.add({
            ...execution,
            startTime: execution.startTime || new Date().toISOString()
        });
    },
    async update(id, changes) {
        return db.pipeline_executions.update(id, changes);
    },
    async getByProject(projectId) {
        return db.pipeline_executions.where('projectId').equals(projectId).toArray();
    },
    async getByPipeline(pipelineId) {
        return db.pipeline_executions.where('pipelineId').equals(pipelineId).toArray();
    },
    async getAll() {
        return db.pipeline_executions.orderBy('startTime').reverse().toArray();
    },
    async getStats() {
        const all = await db.pipeline_executions.toArray();
        const success = all.filter(e => e.status === 'success');
        const errors = all.filter(e => e.status === 'error');
        const totalDuration = success.reduce((s, e) => s + (e.duration || 0), 0);
        const totalTokens = all.reduce((s, e) => s + (e.tokensIn || 0) + (e.tokensOut || 0), 0);
        return {
            total: all.length,
            success: success.length,
            errors: errors.length,
            avgDuration: success.length > 0 ? totalDuration / success.length : 0,
            totalTokens
        };
    },
    async cleanup(olderThanDays = 30) {
        const cutoff = new Date(Date.now() - olderThanDays * 86400000).toISOString();
        return db.pipeline_executions.where('startTime').below(cutoff).delete();
    }
};

// ============================================
// SCOPE AUDIT TRAIL
// ============================================
const auditTrail = {
    async save(record) {
        // record: { projectId, stage, action, itemIndex, field, oldValue, newValue, source }
        return db.scope_audit_trail.add({
            ...record,
            timestamp: new Date().toISOString()
        });
    },
    async getByProject(projectId) {
        return db.scope_audit_trail.where('projectId').equals(projectId).sortBy('timestamp');
    },
    async getByProjectAndStage(projectId, stage) {
        return db.scope_audit_trail.where('[projectId+stage]').equals([projectId, stage]).toArray();
    }
};

// ============================================
// AI CONVERSATIONS (Persistent chat history)
// ============================================
const aiConversations = {
    async save(conversation) {
        const now = new Date().toISOString();
        const existing = conversation?.id ? await db.ai_conversations.get(conversation.id) : null;
        const next = {
            ...(existing || {}),
            ...(conversation || {}),
            status: conversation?.status || existing?.status || 'active',
            created_at: existing?.created_at || conversation?.created_at || now,
            updated_at: now,
        };
        return db.ai_conversations.put(next);
    },
    /**
     * Get the most recent conversation, scoped by project.
     *
     * When `projectId` is supplied, returns the most recent active record
     * with exactly that projectId - or null if the project has no active
     * conversation yet. Importantly, we do NOT fall back to the global most-recent
     * conversation when the scoped lookup misses: doing so caused cross-
     * project leakage (Peter 2026-04-20, "I want each project to have its
     * own conversation with the ai assistant"), where opening the panel
     * on a new project surfaced another project's thread.
     *
     * When `projectId` is null/undefined, returns the most recent
     * *unscoped* conversation (projectId === null) - i.e. the "global"
     * chat thread used on pages where no project is loaded.
     */
    async getCurrent(projectId = null) {
        // With the backend on, a failed server read lands here. Answering
        // from the browser cache made a 502 look like "no thread" (F15).
        if (USE_BACKEND) throw conversationUnavailableError();
        if (projectId) {
            const all = await db.ai_conversations
                .filter(c => c.projectId === projectId && c.status !== 'archived')
                .toArray();
            if (all.length === 0) return null;
            return all.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
        }
        // No project scope → return the most recent truly-global thread only.
        // Audit-hardening (2026-04-20): use strict equality and explicitly
        // exclude records with projectId === undefined. Older records written
        // before the feature have `projectId: undefined`; a `== null` check
        // matched both undefined AND null, leaking stale pre-migration threads
        // into unscoped queries.
        const unscoped = await db.ai_conversations
            .filter(c => c.projectId === null && c.status !== 'archived')
            .toArray();
        if (unscoped.length === 0) return null;
        return unscoped.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
    },
    // The list reads follow getCurrent: with the backend on, a failed server
    // read lands here, and the browser cache (copies from before threads by
    // job, rows the New mirror adds) is never a source of a conversation (I6).
    async getByProject(projectId) {
        if (USE_BACKEND) throw conversationUnavailableError();
        if (!projectId) return [];
        return db.ai_conversations
            .filter(c => c.projectId === projectId)
            .toArray();
    },
    async getAll() {
        if (USE_BACKEND) throw conversationUnavailableError();
        return db.ai_conversations.orderBy('updated_at').reverse().toArray();
    },
    async getRecent(limit = 20) {
        if (USE_BACKEND) throw conversationUnavailableError();
        return db.ai_conversations.orderBy('updated_at').reverse().limit(limit).toArray();
    },
    async delete(id) {
        return db.ai_conversations.delete(id);
    },
    async clearAll() {
        return db.ai_conversations.clear();
    },

    // ── Wave-2 Ship 2 additions ────────────────────────────────────────────
    // These mirror the new backend endpoints. Dexie is updated first (fast,
    // always available, survives offline), then the proxy in createProxy()
    // dual-writes to the API. The proxy-level dual-write is keyed by the
    // method name being in WRITE_METHODS, which is why `append`, `compact`,
    // `archive`, and `createNew` are listed there.
    //
    // Kill-switch (client-side):
    //   localStorage.qm_disable_assistant_compaction === '1'
    //     → `compact` becomes a no-op that returns the conversation as-is.
    // The qm_disable_assistant_persistence switch is retired (QA round 3):
    // with the backend on, the server is the only source of a conversation
    // (spec I6), and a memory-only mode made New and Clear always fail.

    /**
     * Append a message to the active conversation. If no active conversation
     * exists for this projectId, one is created on the fly so callers never
     * need to guard against the first-message race.
     */
    async append(convOrId, message, tokenDelta = 0) {
        // Accept either a full conversation object or a bare id for ergonomics.
        const id = typeof convOrId === 'object' && convOrId ? convOrId.id : convOrId;
        if (!id) return null;
        const existing = await db.ai_conversations.get(id);
        if (!existing) return null;
        const updated = {
            ...existing,
            page: message?.pageContext?.page || existing.page || null,
            stage: message?.pageContext?.stage ?? existing.stage ?? null,
            messages: Array.isArray(existing.messages)
                ? [...existing.messages, message]
                : [message],
            message_count: (existing.message_count || 0) + 1,
            token_count:   (existing.token_count   || 0) + (Number(tokenDelta) || 0),
            updated_at:    new Date().toISOString(),
        };
        await db.ai_conversations.put(updated);
        return updated;
    },

    /**
     * Compact a conversation: folds the supplied summary + cursor into the
     * row. The caller decides when to trigger and what summary to write.
     * this method is the write side only. Kill-switch bypasses the write.
     */
    async compact(convOrId, { summary = null, compactedUpTo = null, tokenCount = null } = {}) {
        if (typeof localStorage !== 'undefined'
            && localStorage.getItem('qm_disable_assistant_compaction') === '1') {
            const id = typeof convOrId === 'object' && convOrId ? convOrId.id : convOrId;
            return id ? db.ai_conversations.get(id) : null;
        }
        const id = typeof convOrId === 'object' && convOrId ? convOrId.id : convOrId;
        if (!id) return null;
        const existing = await db.ai_conversations.get(id);
        if (!existing) return null;
        const next = { ...existing, updated_at: new Date().toISOString(), last_compacted_at: new Date().toISOString() };
        if (summary != null) next.compacted_summary = summary;
        if (Number.isFinite(Number(compactedUpTo))) {
            next.compacted_up_to = Math.max(existing.compacted_up_to || 0, Number(compactedUpTo));
        }
        if (Number.isFinite(Number(tokenCount))) {
            next.token_count = Number(tokenCount);
        }
        await db.ai_conversations.put(next);
        return next;
    },

    /**
     * Mark a conversation archived. Idempotent: calling on an already-
     * archived row is a no-op.
     */
    async archive(convOrId) {
        const id = typeof convOrId === 'object' && convOrId ? convOrId.id : convOrId;
        if (!id) return null;
        const existing = await db.ai_conversations.get(id);
        if (!existing) return null;
        if (existing.status === 'archived') return existing;
        const next = {
            ...existing,
            status: 'archived',
            archived_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        await db.ai_conversations.put(next);
        return next;
    },

    /**
     * Restore an archived conversation as the active one for its scope.
     * Any current active sibling is archived first so the "one active thread"
     * invariant holds for both project-scoped and global assistant chats.
     */
    async restore(convOrId) {
        const id = typeof convOrId === 'object' && convOrId ? convOrId.id : convOrId;
        if (!id) return null;
        const existing = await db.ai_conversations.get(id);
        if (!existing) return null;
        const now = new Date().toISOString();
        const projectId = existing.projectId ?? existing.project_id ?? null;
        const siblings = projectId
            ? await db.ai_conversations.filter(c => (c.projectId ?? c.project_id) === projectId && c.id !== id && c.status !== 'archived').toArray()
            : await db.ai_conversations.filter(c => (c.projectId ?? c.project_id) == null && c.id !== id && c.status !== 'archived').toArray();
        for (const s of siblings) {
            await db.ai_conversations.put({
                ...s,
                status: 'archived',
                archived_at: now,
                updated_at: now,
            });
        }
        const next = {
            ...existing,
            status: 'active',
            archived_at: null,
            updated_at: now,
        };
        await db.ai_conversations.put(next);
        return next;
    },

    /**
     * Archive the current active conversation (if any) for this projectId
     * and create a fresh active one. Returns the new active row.
     */
    async createNew(projectId = null) {
        // Archive any existing active rows for this project: belt-and-
        // braces; the backend partial unique index also enforces this but
        // the client may be offline and we still want a single active row.
        const siblings = projectId
            ? await db.ai_conversations.filter(c => c.projectId === projectId && c.status !== 'archived').toArray()
            : await db.ai_conversations.filter(c => c.projectId == null && c.status !== 'archived').toArray();
        for (const s of siblings) {
            await db.ai_conversations.put({
                ...s,
                status: 'archived',
                archived_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            });
        }
        const now = new Date().toISOString();
        const fresh = {
            projectId: projectId || null,
            status: 'active',
            messages: [],
            message_count: 0,
            compacted_summary: null,
            compacted_up_to: 0,
            token_count: 0,
            created_at: now,
            updated_at: now,
        };
        const id = await db.ai_conversations.add(fresh);
        return { ...fresh, id };
    },

    // ── Threads by job (spec 2026-10-07 section 5.2) ───────────────────────
    // Same names as the API methods so createProxy routes them. With the
    // backend on, the browser cache is never a source of a conversation: a
    // read that fails over to here throws, so the panel says it could not
    // load the conversation instead of starting a second thread offline (I6).
    // The writes are cache mirrors and only touch a row that is already here.
    // Without the backend (offline development) they are the real store.

    async openProjectThread(projectId) {
        if (USE_BACKEND) throw conversationUnavailableError();
        return localOpenThread(projectId || null);
    },
    async openPlatformThread() {
        if (USE_BACKEND) throw conversationUnavailableError();
        return localOpenThread(null);
    },
    async getThread(id) {
        if (USE_BACKEND) throw conversationUnavailableError();
        const row = id ? await db.ai_conversations.get(id) : null;
        if (!row) {
            const err = new Error('Conversation not found');
            err.status = 404;
            throw err;
        }
        return localThreadRow(row);
    },
    async listSummaries({ projectId = null, scope = 'project', limit = 50 } = {}) {
        if (USE_BACKEND) throw conversationUnavailableError();
        const rows = await db.ai_conversations.toArray();
        return rows
            .filter(r => scope === 'all' || (r.projectId ?? null) === (projectId ?? null))
            .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
            .slice(0, Math.max(1, Number(limit) || 50))
            .map((r) => {
                const { messages, ...rest } = localThreadRow(r);
                const firstUser = (messages || []).find(m => m?.role === 'user' && typeof m.content === 'string');
                // The latest user or assistant line, for the History preview.
                const last = [...(messages || [])].reverse().find(m => (m?.role === 'user' || m?.role === 'assistant') && typeof m.content === 'string' && m.content.trim());
                return {
                    ...rest,
                    preview: firstUser ? firstUser.content.slice(0, 160) : '',
                    lastPreview: last ? last.content.slice(0, 160) : '',
                };
            });
    },
    async appendMessages(id, messages) {
        const existing = id ? await db.ai_conversations.get(id) : null;
        if (!existing) return null;
        const have = new Set((existing.messages || []).map(m => m?.id).filter(Boolean));
        const add = (Array.isArray(messages) ? messages : []).filter(m => m?.id && !have.has(m.id));
        const now = new Date().toISOString();
        const next = {
            ...existing,
            messages: [...(existing.messages || []), ...add],
            message_count: (existing.messages || []).length + add.length,
            updated_at: now,
            last_message_at: now,
        };
        await db.ai_conversations.put(next);
        return { id, messageCount: next.message_count, status: next.status, updatedAt: now };
    },
    async patchMessage(id, messageId, patch) {
        const existing = id ? await db.ai_conversations.get(id) : null;
        if (!existing) return null;
        const { id: _ignoredId, role: _ignoredRole, ...rest } = patch || {};
        const now = new Date().toISOString();
        const next = {
            ...existing,
            messages: (existing.messages || []).map(m => (m?.id === messageId ? { ...m, ...rest } : m)),
            updated_at: now,
        };
        await db.ai_conversations.put(next);
        return { id, messageCount: next.messages.length, status: next.status, updatedAt: now };
    },
    async setCompaction(id, { summary = null, throughMessageId = null } = {}) {
        const existing = id ? await db.ai_conversations.get(id) : null;
        if (!existing) return null;
        const next = {
            ...existing,
            compacted_summary: summary,
            compacted_through_id: throughMessageId,
            updated_at: new Date().toISOString(),
        };
        await db.ai_conversations.put(next);
        return localThreadRow(next);
    },
};

function conversationUnavailableError() {
    const err = new Error('QuoteMaker could not be reached to load this conversation.');
    err.code = 'CONVERSATION_UNAVAILABLE';
    return err;
}

function localThreadRow(row) {
    return {
        ...row,
        projectId: row.projectId ?? row.project_id ?? null,
        messages: Array.isArray(row.messages) ? row.messages : [],
        messageCount: Array.isArray(row.messages) ? row.messages.length : 0,
        compactedSummary: row.compacted_summary ?? row.compactedSummary ?? null,
        compactedThroughId: row.compacted_through_id ?? row.compactedThroughId ?? null,
        updatedAt: row.updated_at ?? row.updatedAt ?? null,
        createdAt: row.created_at ?? row.createdAt ?? null,
        lastMessageAt: row.last_message_at ?? row.updated_at ?? null,
        project: null,
    };
}

// Offline development only (no backend): get-or-create the active row.
async function localOpenThread(projectId) {
    const rows = await db.ai_conversations
        .filter(c => (c.projectId ?? null) === projectId && c.status !== 'archived')
        .toArray();
    let row = rows.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0] || null;
    if (!row) {
        const now = new Date().toISOString();
        row = {
            id: (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
                ? globalThis.crypto.randomUUID()
                : `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
            projectId,
            status: 'active',
            messages: [],
            message_count: 0,
            compacted_summary: null,
            compacted_through_id: null,
            created_at: now,
            updated_at: now,
        };
        await db.ai_conversations.put(row);
    }
    // Every message carries a string id from here on (spec 2026-10-07 I2).
    let changed = false;
    const messages = (row.messages || []).map((m) => {
        if (m && typeof m.id === 'string' && m.id) return m;
        changed = true;
        return { ...m, id: `m-local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}` };
    });
    if (changed) {
        row = { ...row, messages };
        await db.ai_conversations.put(row);
    }
    return localThreadRow(row);
}

// ============================================
// COMPANIES (CRM)
// ============================================
const isActiveCrmRecord = row => row?.isActive !== false && row?.is_active !== false
    && !row?.mergedIntoCompanyId && !row?.merged_into_company_id
    && !row?.mergedIntoContactId && !row?.merged_into_contact_id
    && !row?.mergedIntoPropertyId && !row?.merged_into_property_id;

const companies = {
    async unmatchedRequests() {
        throw new Error('Incoming request matching needs a connection to QuoteMaker.');
    },
    async linkRequest() {
        // Backend owns RFQ/job links. The caller refreshes project summaries after success.
        return null;
    },
    async getHistory() {
        throw new Error('Complete client history needs a connection to QuoteMaker.');
    },
    async match() {
        throw new Error('Client matching needs a connection to QuoteMaker. Reconnect and try again.');
    },
    async findOrCreate(_payload, serverResult) {
        if (!serverResult?.row?.id) throw new Error('Confirm the client online before creating a record.');
        await db.companies.put(serverResult.row);
        return serverResult;
    },
    async getAll() { return (await db.companies.orderBy('name').toArray()).filter(isActiveCrmRecord); },
    async getById(id) {
        const row = await db.companies.get(id);
        const survivorId = row?.mergedIntoCompanyId ?? row?.merged_into_company_id;
        return survivorId ? db.companies.get(survivorId) : row;
    },
    // `serverRow` is what PostgreSQL returned (PUT answers RETURNING *). The
    // request body may be partial ({ id, notes }), so it never replaces the
    // cached row: the server row is laid over the existing one.
    async save(company, serverRow) {
        if (serverRow) {
            if (!serverRow.id) return undefined;
            const existing = await db.companies.get(serverRow.id);
            return db.companies.put(cachedCompanyRow(overlayCrmRow(existing, serverRow)));
        }
        if (!company.id) company.id = crypto.randomUUID();
        if (!company.created_at) company.created_at = new Date().toISOString();
        company.updated_at = new Date().toISOString();
        return db.companies.put(overlayCrmRow(await db.companies.get(company.id), company));
    },
    async delete(id) { return db.companies.update(id, { isActive: false, is_active: false }); },
    async search(query) {
        if (!query) return this.getAll();
        const q = query.toLowerCase();
        return db.companies.filter(c => isActiveCrmRecord(c) && (
            (c.name || '').toLowerCase().includes(q) ||
            (c.abn || '').toLowerCase().includes(q)
        )).toArray();
    },

    // ── Duplicate Detection ──
    // Finds groups of companies that are likely duplicates using:
    //   1. Name normalization (strips Pty Ltd, P/L, Limited, The) + bigram Jaccard similarity
    //   2. ABN exact match (when both companies have an ABN)
    // Returns an array of groups - each group has 2+ companies that look like duplicates.
    async findDuplicateCandidates() {
        const extractStrataPlan = (name) => {
            const text = String(name || '');
            const match = text.match(/\b(?:SP|Strata\s*Plan(?:\s*No\.?)?)\s*:?\s*(\d{2,7})\b/i);
            return match?.[1] || '';
        };
        const normalizeName = (name) => (name || '')
            .toLowerCase()
            .replace(/\bpty\s*\.?\s*ltd\.?\b/gi, '')
            .replace(/\bp\/l\b/gi, '')
            .replace(/\blimited\b/gi, '')
            .replace(/\bthe\b/gi, '')
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, ' ')
            .trim();

        const GENERIC_NAME_TOKENS = new Set([
            'body', 'building', 'buildings', 'corp', 'corporate', 'corporation', 'estate', 'group',
            'management', 'manager', 'managers', 'property', 'real', 'service',
            'services', 'strata', 'the', 'owner', 'owners', 'plan', 'sp', 'no',
            'number', 'company', 'co', 'pty', 'ltd', 'unknown', 'unspecified',
        ]);

        const distinctiveTokens = (name) => normalizeName(name)
            .split(/\s+/)
            .filter(token => token && token.length > 1 && /[a-z]/.test(token) && !GENERIC_NAME_TOKENS.has(token));

        const sharesDistinctiveToken = (a, b) => {
            const aTokens = new Set(distinctiveTokens(a));
            if (aTokens.size === 0) return false;
            return distinctiveTokens(b).some(token => aTokens.has(token));
        };

        const getBigrams = (s) => {
            const set = new Set();
            for (let i = 0; i < s.length - 1; i++) set.add(s.substring(i, i + 2));
            return set;
        };

        const similarity = (a, b) => {
            if (!a || !b) return 0;
            if (a === b) return 1;
            const ab = getBigrams(a), bb = getBigrams(b);
            let inter = 0;
            for (const bg of ab) if (bb.has(bg)) inter++;
            const denom = ab.size + bb.size;
            return denom === 0 ? 0 : (2 * inter) / denom;
        };

        const allCompanies = (await db.companies.toArray()).filter(isActiveCrmRecord);
        const THRESHOLD = 0.75;
        const groups = [];
        const grouped = new Set();
        const abnOf = company => String(company.abn || '').replace(/\D/g, '');
        const pairEvidence = (a, b) => {
            const abnA = abnOf(a), abnB = abnOf(b);
            const spA = extractStrataPlan(a.name), spB = extractStrataPlan(b.name);
            // Explicit contradictory identities always outweigh similar wording.
            if ((abnA && abnB && abnA !== abnB) || (spA && spB && spA !== spB)) return null;
            const abnMatch = !!abnA && abnA === abnB;
            const spMatch = !!spA && spA === spB;
            const sim = similarity(normalizeName(a.name), normalizeName(b.name));
            const nameMatch = sim >= THRESHOLD && sharesDistinctiveToken(a.name, b.name);
            return abnMatch || spMatch || nameMatch ? { abnMatch, spMatch, nameMatch, sim } : null;
        };

        for (let i = 0; i < allCompanies.length; i++) {
            if (grouped.has(allCompanies[i].id)) continue;
            const group = [allCompanies[i]];
            const evidence = [];

            for (let j = i + 1; j < allCompanies.length; j++) {
                if (grouped.has(allCompanies[j].id)) continue;
                // Check every member: an incomplete seed must not bridge two
                // records that have conflicting ABNs or strata plans.
                const matches = group.map(member => pairEvidence(member, allCompanies[j]));
                if (matches.some(match => !match)) continue;
                group.push(allCompanies[j]);
                evidence.push(...matches);
                grouped.add(allCompanies[j].id);
            }

            if (group.length > 1) {
                const commonAbn = abnOf(group[0]);
                const commonSp = extractStrataPlan(group[0].name);
                groups.push({
                    companies: group,
                    reason: commonAbn && group.every(company => abnOf(company) === commonAbn)
                        ? 'same ABN'
                        : commonSp && group.every(company => extractStrataPlan(company.name) === commonSp)
                            ? `same strata plan SP ${commonSp}`
                            : evidence.every(match => match.nameMatch)
                                ? `${Math.round(Math.min(...evidence.map(match => match.sim)) * 100)}% name similarity`
                                : 'Mixed matching details — review each pair',
                });
                grouped.add(allCompanies[i].id);
            }
        }
        return groups;
    },

    // ── Merge Preview (dry run) ──
    // Identity decisions require the authoritative tenant database.
    async previewMerge() {
        throw new Error('Reconnect to QuoteMaker to review a client merge.');
    },
    async mergeCompanies(_keepId, _mergeId, serverResult) {
        return mirrorCompanyMerge(db, serverResult);
    },

    // ── Tidy up (spec 2026-09-23 section 3) ──
    // The rules need the whole tenant database, so offline there is nothing
    // to propose and nothing is applied locally. After an online apply the
    // proxy passes the server's receipt, and the changed rows are mirrored.
    async cleanupProposals() {
        return {
            generatedAt: new Date().toISOString(), duplicates: [], ownersCorporations: [], notClients: [],
            firmsToCreate: [], contactsToMove: [], jobsToMove: [],
            duplicatePeople: [], emailsToClear: [], peopleAsClients: [], offline: true,
        };
    },
    async applyCleanup(actions, serverResult) {
        if (serverResult) {
            await mirrorCompanyCleanup(actions, serverResult);
            return serverResult;
        }
        const list = Array.isArray(actions) ? actions : [];
        return {
            results: list.map((_action, index) => ({ index, ok: false, code: 'OFFLINE', error: 'offline' })),
            applied: 0,
            failed: list.length,
        };
    },
    async managed(id) {
        const rows = await db.companies.filter(c => isActiveCrmRecord(c)
            && (c.managingCompanyId ?? c.managing_company_id) === id).toArray();
        return rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    },
    async setManager(_id, _managingCompanyId, serverRow) {
        if (!serverRow?.id) throw new Error('Linking a building to its managing firm needs a connection to QuoteMaker.');
        // Mirror the row PostgreSQL returned; its id is the server's, never
        // an id read back off the caller's arguments.
        await db.companies.put(cachedCompanyRow(serverRow));
        return serverRow;
    },
    async setType(_id, _companyType, serverRow) {
        if (!serverRow?.id) throw new Error("Changing a record's type needs a connection to QuoteMaker.");
        await db.companies.put(cachedCompanyRow(serverRow));
        return serverRow;
    },
    // The firm predicate lives on the server (one list, one rule), so there
    // is no offline approximation of it.
    async firmCandidates() {
        throw new Error('The managing firm list needs a connection to QuoteMaker.');
    },
};

// Lay `next` over a cached row. Older cached rows hold both spellings of a
// field, so each written value also replaces its other spelling when the
// cached row carries it; a stale `payment_terms` never outlives `paymentTerms`.
const toSnakeKey = key => key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
const toCamelKey = key => key.replace(/_([a-z0-9])/g, (_, letter) => letter.toUpperCase());
function overlayCrmRow(existing, next) {
    const merged = { ...(existing || {}) };
    for (const [key, value] of Object.entries(next || {})) {
        merged[key] = value;
        for (const spelling of [toSnakeKey(key), toCamelKey(key)]) {
            if (spelling !== key && Object.hasOwn(merged, spelling)) merged[spelling] = value;
        }
    }
    return merged;
}

// A company row as the browser cache keeps it: the server row (camelCase from
// apiFetch) plus the snake_case spellings older cached rows and the Clients
// page read for the identity fields.
function cachedCompanyRow(row) {
    const isActive = row.isActive ?? row.is_active;
    const companyType = row.companyType ?? row.company_type;
    const managingCompanyId = row.managingCompanyId ?? row.managing_company_id ?? null;
    return {
        ...row,
        isActive, is_active: isActive,
        companyType, company_type: companyType,
        managingCompanyId, managing_company_id: managingCompanyId,
        merged_into_company_id: row.mergedIntoCompanyId ?? row.merged_into_company_id ?? null,
    };
}

// Mirror a whole Tidy up apply in ONE pass over the cached tables (the single
// merge path, mirrorCompanyMerge, scans them per merge; an apply can carry
// dozens of merges). Rows are written in the order the server applied the
// actions (merges, then classifications, then archives), so the newest
// snapshot of a row wins.
async function mirrorCompanyCleanup(actions, serverResult) {
    const rank = { merge: 0, classify: 1, archive: 2, createFirm: 3, moveContact: 4, moveJob: 5, clearEmail: 6, mergePeople: 7 };
    const list = Array.isArray(actions) ? actions : [];
    const applied = (serverResult?.results || [])
        .filter(item => item?.ok && !item.noop && item.cacheUpdates)
        .sort((a, b) => (rank[list[a.index]?.kind] ?? 9) - (rank[list[b.index]?.kind] ?? 9) || a.index - b.index);
    if (!applied.length) return;
    const survivorOf = new Map();
    for (const item of applied) {
        if (item.keptCompanyId && item.archivedCompanyId) survivorOf.set(item.archivedCompanyId, item.keptCompanyId);
    }
    // A was merged into B, then B into C: A's links end at C.
    const survivor = (id) => {
        let current = id;
        for (let hops = 0; survivorOf.has(current) && hops < 50; hops += 1) current = survivorOf.get(current);
        return current;
    };
    const tables = ['companies', 'crm_contacts', 'projects', 'properties', 'company_addresses', 'crm_activities', 'crm_insights'];
    await db.transaction('rw', tables.map(name => db[name]), async () => {
        if (survivorOf.size) {
            // Both spellings exist in older caches; quote content and its
            // concurrency receipt are left untouched.
            for (const table of ['projects', 'properties', 'company_addresses', 'crm_activities']) {
                for (const row of await db[table].toArray()) {
                    const companyId = row.companyId ?? row.company_id;
                    if (survivorOf.has(companyId)) {
                        const keepId = survivor(companyId);
                        await db[table].update(row.id, { companyId: keepId, company_id: keepId });
                    }
                }
            }
            for (const row of await db.crm_insights.toArray()) {
                const scopeId = row.scopeId ?? row.scope_id;
                if ((row.scopeType ?? row.scope_type) === 'company' && survivorOf.has(scopeId)) {
                    const keepId = survivor(scopeId);
                    await db.crm_insights.update(row.id, { scopeId: keepId, scope_id: keepId });
                }
            }
        }
        for (const item of applied) {
            for (const row of item.cacheUpdates.companies || []) {
                if (row?.id) await db.companies.put(cachedCompanyRow(row));
            }
            for (const row of item.cacheUpdates.contacts || []) {
                if (!row?.id) continue;
                await db.crm_contacts.put({ ...row,
                    company_id: row.companyId ?? row.company_id,
                    is_active: row.isActive ?? row.is_active,
                    is_primary: row.isPrimary ?? row.is_primary,
                    merged_into_contact_id: row.mergedIntoContactId ?? row.merged_into_contact_id ?? null,
                });
            }
            // A job moved from a building to its firm (Tidy up, spec 2026-10-08
            // D8). Only its two CRM keys change; its content and save token stay.
            for (const row of item.cacheUpdates.projects || []) {
                if (!row?.id || !(await db.projects.get(row.id))) continue;
                const companyId = row.companyId ?? row.company_id ?? null;
                const propertyId = row.propertyId ?? row.property_id ?? null;
                await db.projects.update(row.id, { companyId, company_id: companyId, propertyId, property_id: propertyId });
            }
        }
    });
}

// ============================================
// COMPANY ADDRESSES (CRM)
// ============================================
const companyAddresses = {
    async getByCompany(companyId) { return db.company_addresses.where('company_id').equals(companyId).toArray(); },
    async save(addr) { return db.company_addresses.put(addr); },
    async delete(id) { return db.company_addresses.delete(id); },
};

// ============================================
// CRM CONTACTS
// ============================================
const crmContacts = {
    async getAll() { return (await db.crm_contacts.orderBy('created_at').reverse().toArray()).filter(isActiveCrmRecord); },
    async getById(id) { return db.crm_contacts.get(id); },
    async getByCompany(companyId) { return (await db.crm_contacts.where('company_id').equals(companyId).toArray()).filter(isActiveCrmRecord); },
    async save(contact) {
        if (!contact.id) contact.id = crypto.randomUUID();
        if (!contact.created_at) contact.created_at = new Date().toISOString();
        contact.updated_at = new Date().toISOString();
        return db.crm_contacts.put(contact);
    },
    async delete(id) { return db.crm_contacts.update(id, { isActive: false, is_active: false }); },
    async search(query) {
        if (!query) return this.getAll();
        const q = query.toLowerCase();
        return db.crm_contacts.filter(c => isActiveCrmRecord(c) && (
            (c.first_name || '').toLowerCase().includes(q) ||
            (c.last_name || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q)
        )).toArray();
    },
};

// ============================================
// CRM ACTIVITIES
// ============================================
const crmActivities = {
    async log(activity) {
        if (!activity.created_at) activity.created_at = new Date().toISOString();
        return db.crm_activities.add(activity);
    },
    async getByCompany(companyId, limit = 50) {
        return db.crm_activities.where('company_id').equals(companyId).sortBy('created_at').then(r => { r.reverse(); return r.slice(0, limit); });
    },
    async getByProject(projectId) {
        return db.crm_activities.where('project_id').equals(projectId).toArray();
    },
    async getPendingFollowUps() {
        return db.crm_activities.where('activity_type').equals('follow_up').filter(a => !a.completed_at).toArray();
    },
    async completeFollowUp(id) {
        return db.crm_activities.update(id, { completed_at: new Date().toISOString() });
    },
};

// ============================================
// PROPERTIES (Sites/Buildings)
// ============================================
const properties = {
    async getAll() { return (await db.properties.orderBy('created_at').reverse().toArray()).filter(isActiveCrmRecord); },
    async getById(id) {
        const row = await db.properties.get(id);
        const survivorId = row?.mergedIntoPropertyId ?? row?.merged_into_property_id;
        return survivorId ? db.properties.get(survivorId) : row;
    },
    async getByCompany(companyId) { return (await db.properties.where('company_id').equals(companyId).toArray()).filter(isActiveCrmRecord); },
    async getByStrataplan(sp) {
        if (!sp) return [];
        const normalized = sp.replace(/\s+/g, '').toUpperCase();
        return db.properties.filter(p =>
            isActiveCrmRecord(p) && p.strata_plan && p.strata_plan.replace(/\s+/g, '').toUpperCase() === normalized
        ).toArray();
    },
    async save(property) {
        // Validate required fields (parity with backend validation)
        if (!property.id && !property.address_line1 && !property.name) {
            throw new Error('Property requires at least a name or address_line1');
        }
        if (!property.id && !property.company_id) {
            throw new Error('Property requires a company_id');
        }
        if (property.condition_score != null) {
            const score = parseFloat(property.condition_score);
            if (isNaN(score) || score < 1.0 || score > 10.0) {
                throw new Error('condition_score must be between 1.0 and 10.0');
            }
        }
        if (!property.id) property.id = crypto.randomUUID();
        if (!property.created_at) property.created_at = new Date().toISOString();
        property.updated_at = new Date().toISOString();
        return db.properties.put(property);
    },
    async delete(id) { return db.properties.update(id, { isActive: false, is_active: false }); },
    async search(query) {
        if (!query) return this.getAll();
        const q = query.toLowerCase();
        return db.properties.filter(p => isActiveCrmRecord(p) && (
            (p.name || '').toLowerCase().includes(q) ||
            (p.address_line1 || '').toLowerCase().includes(q) ||
            (p.strata_plan || '').toLowerCase().includes(q) ||
            (p.suburb || '').toLowerCase().includes(q)
        )).toArray();
    },
    async findByAddress(address) {
        if (!address) return [];
        // Use the shared normalizer from utils/address.js for consistency
        const { normalizeAddress, addressSimilarity } = await import('../utils/address.js');
        const normAddr = normalizeAddress(address);
        if (!normAddr) return [];
        return db.properties.filter(p => {
            if (!isActiveCrmRecord(p)) return false;
            const normP = normalizeAddress(p.address_line1);
            if (!normP) return false;
            // Use word-boundary-safe matching: require the street number to match
            // at the START of the string to avoid "12" matching "112" or "312"
            // Then use Jaccard similarity for the rest (threshold 0.6)
            const sim = addressSimilarity(address, p.address_line1);
            if (sim >= 0.8) return true; // High similarity - very likely same building
            // Also match if normalized strings are equal (exact match after normalization)
            if (normP === normAddr) return true;
            return false;
        }).toArray();
    },
    // NOTE: intra-company Site dedup ("Tidy Sites") is intentionally not
    // exposed here. The v5.12.0 proxy routes WRITES to the API path; doing
    // multi-row Site re-pointing locally would silently diverge from
    // Postgres. If we add this surface back, the backend needs a paired
    // /properties/dedupe-for-company endpoint that runs the same logic
    // server-side inside one transaction.
};

// ============================================
// PROPERTY CONTACTS (Junction)
// ============================================
const propertyContacts = {
    async getByProperty(propertyId) { return db.property_contacts.where('property_id').equals(propertyId).toArray(); },
    async getByContact(contactId) { return db.property_contacts.where('contact_id').equals(contactId).toArray(); },
    async save(record) {
        // Validate required FKs (parity with backend)
        if (!record.property_id) throw new Error('property_id is required');
        if (!record.contact_id) throw new Error('contact_id is required');
        if (!record.id) record.id = crypto.randomUUID();
        if (!record.created_at) record.created_at = new Date().toISOString();
        return db.property_contacts.put(record);
    },
    async delete(id) { return db.property_contacts.delete(id); },
    async deleteByProperty(propertyId) {
        return db.property_contacts.where('property_id').equals(propertyId).delete();
    },
};

// ============================================
// CRM INSIGHTS - AI-generated portfolio insights
// ============================================
const crmInsights = {
    async getAll() { return db.crm_insights.orderBy('generated_at').reverse().toArray(); },
    async getActive() {
        const all = await db.crm_insights.toArray();
        const now = Date.now();
        return all.filter(i => !i.is_dismissed && (!i.expires_at || new Date(i.expires_at).getTime() > now))
            .sort((a, b) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime());
    },
    async getByScope(scopeType, scopeId = null) {
        // Normalize undefined → null for consistent behavior at the entry point.
        // All internal code paths now use null explicitly.
        const normalizedScopeId = scopeId == null ? null : scopeId;

        // Dexie compound index with null values is browser-dependent and unreliable.
        // For null scope_id (global insights), use simple index + JS filter.
        let results;
        if (normalizedScopeId === null) {
            const allOfType = await db.crm_insights.where('scope_type').equals(scopeType).toArray();
            // Match only records with explicitly null/undefined scope_id
            results = allOfType.filter(i => i.scope_id == null);
        } else {
            results = await db.crm_insights.where('[scope_type+scope_id]').equals([scopeType, normalizedScopeId]).toArray();
        }
        const now = Date.now();
        return results
            .filter(i => !i.is_dismissed && (!i.expires_at || new Date(i.expires_at).getTime() > now))
            .sort((a, b) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime());
    },
    async getById(id) { return db.crm_insights.get(id); },
    async save(insight) {
        if (!insight.id) insight.id = crypto.randomUUID();
        if (!insight.generated_at) insight.generated_at = new Date().toISOString();
        if (!insight.created_at) insight.created_at = new Date().toISOString();
        insight.updated_at = new Date().toISOString();
        return db.crm_insights.put(insight);
    },
    async dismiss(id) {
        return db.crm_insights.update(id, {
            is_dismissed: true,
            dismissed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        });
    },
    async pin(id, pinned = true) {
        return db.crm_insights.update(id, {
            is_pinned: pinned,
            updated_at: new Date().toISOString(),
        });
    },
    async delete(id) { return db.crm_insights.delete(id); },
    async clearExpired() {
        const now = Date.now();
        const all = await db.crm_insights.toArray();
        const expired = all.filter(i => i.expires_at && new Date(i.expires_at).getTime() < now && !i.is_pinned);
        for (const i of expired) await db.crm_insights.delete(i.id);
        return expired.length;
    },
};

const insightRuns = {
    async start(scopeType, scopeId, trigger = 'manual') {
        const run = {
            scope_type: scopeType,
            scope_id: scopeId,
            trigger,
            status: 'running',
            started_at: new Date().toISOString(),
        };
        const id = await db.insight_runs.add(run);
        return { ...run, id };
    },
    async complete(runId, result = {}) {
        const updates = {
            status: result.status || 'success',
            insights_generated: result.generated || 0,
            insights_updated: result.updated || 0,
            duration_ms: result.durationMs || 0,
            ai_model: result.model || null,
            tokens_used: result.tokens || 0,
            cost_estimate: result.cost || 0,
            error_message: result.error || null,
            completed_at: new Date().toISOString(),
        };
        return db.insight_runs.update(runId, updates);
    },
    async getRecent(limit = 20) {
        return db.insight_runs.orderBy('started_at').reverse().limit(limit).toArray();
    },
};

const insightInteractions = {
    async log(insightId, interactionType, metadata = {}) {
        return db.insight_interactions.add({
            insight_id: insightId,
            interaction_type: interactionType,
            metadata,
            created_at: new Date().toISOString(),
        });
    },
    async getByInsight(insightId) {
        return db.insight_interactions.where('insight_id').equals(insightId).sortBy('created_at').then(r => { r.reverse(); return r; });
    },
};

// ============================================
// BIOS
// ============================================
function flattenBioEnvelope(payload = {}, extra = {}) {
    const { data, ...envelope } = payload || {};
    const body = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    return Object.fromEntries(
        Object.entries({ ...body, ...envelope, ...extra })
            .filter(([, value]) => value !== undefined)
    );
}

const bios = {
    business: {
        async get() {
            return db.business_profile.get('current');
        },
        async save(payload = {}) {
            const row = {
                id: 'current',
                ...flattenBioEnvelope(payload),
                updated_at: payload.updated_at || payload.updatedAt || new Date().toISOString(),
            };
            await db.business_profile.put(row);
            return row;
        },
    },
    client: {
        async getAll(companyId = null) {
            if (companyId) return db.client_bios.where('companyId').equals(companyId).toArray();
            return db.client_bios.toArray();
        },
        async getById(id) {
            return db.client_bios.get(id);
        },
        async save(id, payload = {}) {
            const existing = await db.client_bios.get(id);
            const row = {
                ...(existing || {}),
                ...flattenBioEnvelope(payload, {
                    id,
                    companyId: payload.companyId || payload.company_id || existing?.companyId,
                }),
                updated_at: payload.updated_at || payload.updatedAt || new Date().toISOString(),
            };
            await db.client_bios.put(row);
            return row;
        },
        async delete(id) {
            return db.client_bios.delete(id);
        },
    },
    strata: {
        async getAll({ strataPlan = null, address = null } = {}) {
            if (strataPlan) return db.strata_bios.where('strataPlan').equals(strataPlan).toArray();
            if (address) return db.strata_bios.where('address').equals(address).toArray();
            return db.strata_bios.toArray();
        },
        async getById(id) {
            return db.strata_bios.get(id);
        },
        async save(id, payload = {}) {
            const existing = await db.strata_bios.get(id);
            const row = {
                ...(existing || {}),
                ...flattenBioEnvelope(payload, {
                    id,
                    strataPlan: payload.strataPlan || payload.strata_plan || existing?.strataPlan,
                    address: payload.address || existing?.address,
                }),
                updated_at: payload.updated_at || payload.updatedAt || new Date().toISOString(),
            };
            await db.strata_bios.put(row);
            return row;
        },
        async delete(id) {
            return db.strata_bios.delete(id);
        },
    },
};

// ============================================
// LOCAL (Dexie) DATA SERVICE
// ============================================
const DataServiceLocal = {
    quotes,
    drafts,
    projects,
    businessPricing,
    clients,
    receivables,
    receivableEvidence,
    receivableProjectLinks,
    receivableActions,
    receivableSyncState,
    pipelines,
    auditTrail,
    aiConversations,
    companies,
    companyAddresses,
    crmContacts,
    crmActivities,
    properties,
    propertyContacts,
    bios,
    crmInsights,
    insightRuns,
    insightInteractions,

    // Tasks page (Dexie v31, SPEC-tasks-page.md slice 1) — browser-local BY
    // DESIGN until the server floor (migration 085_tasks.sql, slice 4) and
    // the promotion to synced (slice 5) land. All seven methods are listed in
    // LOCAL_ONLY_METHODS below; slice 5 removes those entries, adds
    // 'setStatus' to WRITE_METHODS, and adds the tasks proxy mirror branch —
    // all in ONE atomic commit (a half-shipped pair throws on the first save).
    //
    // Writes take and RETURN full rows (put semantics) so the slice-5 mirror
    // branch can reuse these functions to mirror CANONICAL server rows.
    tasks: {
        async save(task) {
            if (!task || typeof task !== 'object') throw new Error('tasks.save: row object required');
            const row = { ...task };
            if (!row.id) row.id = crypto.randomUUID();
            if (!row.title || !String(row.title).trim()) throw new Error('tasks.save: title required');
            if (!row.status) row.status = 'open';
            if (!row.source) row.source = 'typed';
            if (!row.createdAt) row.createdAt = new Date().toISOString();
            row.updatedAt = new Date().toISOString();
            await db.tasks.put(row);
            return row;
        },
        async bulkSave(tasks) {
            const now = new Date().toISOString();
            const rows = (Array.isArray(tasks) ? tasks : []).map((t) => ({
                status: 'open',
                source: 'typed',
                createdAt: now,
                ...t,
                id: t?.id || crypto.randomUUID(),
                updatedAt: now,
            }));
            await db.tasks.bulkPut(rows);
            return rows;
        },
        // Status is a state machine, never a bare field write (backend route
        // discipline Rule 3, applied Dexie-side so slice 5 changes nothing
        // behaviourally). Same-status calls are idempotent and return the row
        // with `already: true` (crm-activities precedent).
        async setStatus(id, status, reason = null) {
            const TRANSITIONS = {
                open: new Set(['done', 'dropped']),
                done: new Set(['open']),
                dropped: new Set(['open']),
            };
            const current = await db.tasks.get(id);
            if (!current) throw new Error(`tasks.setStatus: no task with id ${id}`);
            if (current.status === status) return { ...current, already: true };
            if (!TRANSITIONS[current.status] || !TRANSITIONS[current.status].has(status)) {
                throw new Error(`tasks.setStatus: cannot move ${current.status} to ${status}`);
            }
            const closing = status === 'done' || status === 'dropped';
            const row = {
                ...current,
                status,
                closedAt: closing ? new Date().toISOString() : null,
                closeReason: closing
                    ? (reason || (status === 'dropped' ? 'dropped' : 'by_operator'))
                    : null,
                // Reopening also clears the snooze so a revived task is fully live.
                snoozedUntil: status === 'open' ? null : (current.snoozedUntil ?? null),
                updatedAt: new Date().toISOString(),
            };
            await db.tasks.put(row);
            return row;
        },
        async getAll() { return db.tasks.orderBy('createdAt').reverse().toArray(); },
        async getOpen() { return db.tasks.where('status').equals('open').toArray(); },
        async getByProject(projectId) { return db.tasks.where('projectId').equals(projectId).toArray(); },
        async getBySourceKeys(keys) {
            const list = (Array.isArray(keys) ? keys : []).filter(Boolean);
            if (list.length === 0) return [];
            return db.tasks.where('sourceKey').anyOf(list).toArray();
        },
    },

    // Sync state (v11) - tracks what's been synced to S3/MinIO
    syncState: {
        async get(id) {
            return db.sync_state.get(id);
        },
        async getByProject(projectId) {
            return db.sync_state.where('projectId').equals(projectId).toArray();
        },
        async getByPath(path) {
            return db.sync_state.where('path').equals(path).first();
        },
        async getAll() {
            return db.sync_state.toArray();
        },
        async save(record) {
            if (!record.id) {
                // Use path as natural key: "projectId/relative/path.json"
                record.id = record.path || ('ss_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
            }
            record.lastSyncedAt = record.lastSyncedAt || new Date().toISOString();
            return db.sync_state.put(record);
        },
        async delete(id) {
            return db.sync_state.delete(id);
        },
        async deleteByProject(projectId) {
            return db.sync_state.where('projectId').equals(projectId).delete();
        },
        async clear() {
            return db.sync_state.clear();
        },
    },

    // Prompt versions (v11) - prompt version history with performance telemetry
    promptVersions: {
        async save(version) {
            if (!version.created_at) version.created_at = new Date().toISOString();
            return db.prompt_versions.add(version);
        },
        async update(id, changes) {
            return db.prompt_versions.update(id, changes);
        },
        async getByPrompt(promptId) {
            const rows = await db.prompt_versions.where('promptId').equals(promptId)
                .sortBy('version');
            rows.reverse();
            return rows;
        },
        async getLatest(promptId) {
            const versions = await db.prompt_versions.where('promptId').equals(promptId)
                .sortBy('version');
            return versions[versions.length - 1] || null;
        },
        async getAll() {
            return db.prompt_versions.orderBy('created_at').reverse().toArray();
        },
        async delete(id) {
            return db.prompt_versions.delete(id);
        },
        async getStats(promptId) {
            const versions = await db.prompt_versions.where('promptId').equals(promptId).toArray();
            return {
                promptId,
                versionCount: versions.length,
                latestVersion: versions.length > 0
                    ? Math.max(...versions.map(v => v.version || 0))
                    : 0,
            };
        },
    },

    // Founder strategies (v12) - Strategic Brain directives with elevated prompt weight
    // ALL queries are tenant-scoped via getActiveTenantId() guardrail.
    founderStrategies: {
        async save(strategy) {
            const { getActiveTenantId, stampTenantId } = await import('./tenant-resolver');
            if (!strategy.id) strategy.id = 'fs_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            if (!strategy.created_at) strategy.created_at = new Date().toISOString();
            strategy.updated_at = new Date().toISOString();
            if (strategy.is_active === undefined) strategy.is_active = true;
            if (strategy.priority === undefined) strategy.priority = 5;
            if (strategy.use_count === undefined) strategy.use_count = 0;
            stampTenantId(strategy);
            return db.founder_strategies.put(strategy);
        },
        async getAll() {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            return db.founder_strategies.where('tenant_id').equals(tid)
                .toArray()
                .then(arr => arr.sort((a, b) => (b.priority || 0) - (a.priority || 0)));
        },
        async getActive() {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            const all = await db.founder_strategies
                .where('[tenant_id+is_active]').equals([tid, 1])
                .toArray();
            // Dexie stores booleans as 0/1 in compound indexes, so also filter
            // in JS for safety (true vs 1 coercion edge cases)
            const allForTenant = all.length > 0
                ? all
                : (await db.founder_strategies.where('tenant_id').equals(tid).toArray())
                    .filter(s => s.is_active !== false);
            return allForTenant
                .filter(s => s.is_active !== false)
                .sort((a, b) => (b.priority || 0) - (a.priority || 0));
        },
        async getById(id) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const s = await db.founder_strategies.get(id);
            if (!s || s.tenant_id !== getActiveTenantId()) return null;
            return s;
        },
        async getByCategory(category) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            return (await db.founder_strategies.where('category').equals(category).toArray())
                .filter(s => s.tenant_id === tid);
        },
        async delete(id) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const s = await db.founder_strategies.get(id);
            if (!s || s.tenant_id !== getActiveTenantId()) return;
            return db.founder_strategies.delete(id);
        },
        async toggleActive(id) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const s = await db.founder_strategies.get(id);
            if (!s || s.tenant_id !== getActiveTenantId()) return null;
            s.is_active = !s.is_active;
            s.updated_at = new Date().toISOString();
            await db.founder_strategies.put(s);
            return s;
        },
        async incrementUseCount(id) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const s = await db.founder_strategies.get(id);
            if (!s || s.tenant_id !== getActiveTenantId()) return;
            s.use_count = (s.use_count || 0) + 1;
            s.last_used_at = new Date().toISOString();
            await db.founder_strategies.put(s);
        },
        async getStats() {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            const all = (await db.founder_strategies.where('tenant_id').equals(tid).toArray());
            const active = all.filter(s => s.is_active !== false);
            const byCategory = {};
            for (const s of all) byCategory[s.category] = (byCategory[s.category] || 0) + 1;
            return {
                total: all.length,
                active: active.length,
                byCategory,
                totalUseCount: all.reduce((sum, s) => sum + (s.use_count || 0), 0),
            };
        },
    },

    // Brain telemetry (v12) - records which strategies fired per pipeline run
    // ALL queries are tenant-scoped via getActiveTenantId() guardrail.
    brainTelemetry: {
        async save(record) {
            const { stampTenantId } = await import('./tenant-resolver');
            if (!record.created_at) record.created_at = new Date().toISOString();
            stampTenantId(record);
            return db.brain_telemetry.add(record);
        },
        async getByProject(projectId) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            return (await db.brain_telemetry.where('projectId').equals(projectId).toArray())
                .filter(r => r.tenant_id === tid)
                .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        },
        async getRecent(limit = 50) {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            const rows = await db.brain_telemetry.where('tenant_id').equals(tid)
                .sortBy('created_at');
            rows.reverse();
            return rows.slice(0, limit);
        },
        async getAll() {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            return db.brain_telemetry.where('tenant_id').equals(tid)
                .sortBy('created_at').then(r => { r.reverse(); return r; });
        },
        async getStats() {
            const { getActiveTenantId } = await import('./tenant-resolver');
            const tid = getActiveTenantId();
            const all = await db.brain_telemetry.where('tenant_id').equals(tid).toArray();
            const totalStrategiesFired = all.reduce((sum, r) => sum + (r.strategies_fired?.length || 0), 0);
            const totalRuns = all.length;
            return {
                totalRuns,
                totalStrategiesFired,
                avgFired: totalRuns > 0 ? Math.round((totalStrategiesFired / totalRuns) * 10) / 10 : 0,
            };
        },
    },

    // Plausibility Fix Center resolutions (v21): per-flag status so the
    // banner stops nagging once a flag is fixed / ignored / acknowledged.
    plausibilityResolutions: {
        async get(projectId, flagHash) {
            if (!projectId || !flagHash) return null;
            // why: compound index lookup is O(log n) vs full scan
            const rows = await db.plausibility_resolutions
                .where('[projectId+flagHash]').equals([projectId, flagHash]).toArray();
            return rows[0] || null;
        },
        async put(record) {
            if (!record || !record.projectId || !record.flagHash) return null;
            if (!record.id) record.id = 'pr_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
            record.updated_at = new Date().toISOString();
            if (!record.timestamp) record.timestamp = record.updated_at;
            // why: upsert-by-(projectId,flagHash) so re-resolving a flag replaces the prior row
            const existing = await db.plausibility_resolutions
                .where('[projectId+flagHash]').equals([record.projectId, record.flagHash]).first();
            if (existing) record.id = existing.id;
            await db.plausibility_resolutions.put(record);
            return record.id;
        },
        async listByProject(projectId) {
            if (!projectId) return [];
            return db.plausibility_resolutions.where('projectId').equals(projectId).toArray();
        },
        async delete(id) {
            return db.plausibility_resolutions.delete(id);
        },
    },
};

// ============================================
// EXPORT - Toggle between Dexie (local) and API (backend) via env var
// ============================================
// Backend mode is the default - PostgreSQL is the single source of truth.
// Set VITE_USE_BACKEND=false only for offline development without Docker.
//
// ── Intentionally Local Data (not routed to PG) ──
//   • app_settings (Dexie) - FileSystemDirectoryHandle for folder sync (non-serialisable)
//   • qm_quick_notes, qm_show_costs (localStorage) - per-browser UI preferences
//   • qm_benchmark_override_snapshot (localStorage) - transient crash-recovery
const USE_BACKEND = import.meta.env?.VITE_USE_BACKEND !== 'false';

// ─── v5.12.0 (2026-04-22): backend is the single source of truth ──────────
// Peter RJ Bird pilot. The previous dual-source design (Dexie-first failover
// + outbox queue + 3-strike kill switch) silently diverged Postgres and
// Dexie. Writes that failed the backend got queued with Dexie IDs that
// Postgres could never ingest (FK/UUID-syntax violations). That risked
// pilot data integrity: Machine A's local work never synced to Machine B.
//
// New model:
//   • Writes are API-only. On success → dual-write to Dexie (read cache).
//     On failure → surface a visible banner + throw to the caller. NEVER
//     silently fall back to Dexie for writes.
//   • Reads are API-first. On unreachable-class failure → fall back to
//     the Dexie cache (last known good). 404s propagate as "not found",
//     not as connectivity failure.
//   • `disabled` = "backend is currently unreachable for writes". Clears
//     on any successful backend op. No more 3-strike latch, no outbox.
//
// This module purposefully does NOT carry a queue. If Docker is down,
// the user sees a loud banner and writes fail until Docker comes back.
// That is correct behaviour for a local-Docker deployment where the
// backend is the product.
let _apiService = null;
let _apiLoaded = false;
let _apiLoadPromise = null;
// `_apiDisabled` ("the last WRITE failed with an unreachable-class error")
// now lives in network-resilience.ts as the backend-reachability signal, so
// apiFetch can clear it from a surface that never touches this proxy. The
// helpers below keep this module's `disabled` vocabulary and public API.

// Classify errors so we only trip the banner on genuine connectivity failures.
// Data-shape problems (400/422) and access errors (401/403) are NOT banner-worthy;
// 404 means "not found" and 500 means "route bug/server error"; neither proves
// Docker or the gateway is offline, so keep the disconnected banner for transport
// and gateway availability failures only.
export function isUnreachableError(err) {
    const status = err?.status;
    if (status === 502 || status === 503 || status === 504) return true;
    // apiFetch and the upload lanes normalise a dropped request to status 0 +
    // NETWORK_UNREACHABLE (network-resilience.ts); the message no longer
    // says "Failed to fetch", it tells the operator to check their signal.
    if (status === 0) return true;
    if (err?.code === 'NETWORK_UNREACHABLE') return true;
    const msg = err?.message || String(err || '');
    if (/Failed to fetch/i.test(msg)) return true;
    if (/NetworkError/i.test(msg)) return true;
    if (/ECONNREFUSED|ETIMEDOUT|ENOTFOUND/i.test(msg)) return true;
    if (/\bAbortError\b/i.test(msg) && /timeout/i.test(msg)) return true;
    return false;
}

function _apiStatusSnapshot() {
    const disabled = !isBackendReachable();
    return { disabled, failCount: disabled ? 1 : 0 };
}
function _emitApiStatus() {
    emitBackendReachability();
}
function _setDisabled(next) {
    setBackendReachable(!next);
}
export function getApiStatus() {
    return _apiStatusSnapshot();
}
export function subscribeApiStatus(cb) {
    if (typeof cb !== 'function') return () => {};
    const unsubscribe = subscribeBackendReachability(() => {
        try { cb(_apiStatusSnapshot()); } catch (e) { console.warn('[DataService] status subscriber threw:', e?.message); }
    });
    try { cb(_apiStatusSnapshot()); } catch {}
    return unsubscribe;
}
/**
 * The backend answered: take the "Backend unreachable" banner down. apiFetch
 * calls this on every 2xx (idempotent: a no-op unless the banner is up), so a
 * surface that bypasses the DataService proxy still clears the banner its own
 * successes disprove. Field report 2026-09-22: the banner latched at 08:11 and
 * sat over a Site Visits surface that was uploading photos fine.
 */
export function markBackendReachable() {
    _setDisabled(false);
}
/**
 * Re-acquire a backend access token + clear any stale "unreachable" banner.
 * Called by the banner's "Reconnect" button. Returns true on success.
 *
 * Name kept for backwards compatibility with App.jsx. Internally this is
 * now a lightweight auth refresh + status reset. No kill-switch, no outbox.
 */
const RECONNECT_PROBE_TIMEOUT_MS = 8000;
export async function retryBackendLogin() {
    try {
        // "Reconnected" must mean the backend ANSWERED. On a cloud origin
        // devAutoLogin returns without a network call whenever the stored
        // access token is unexpired, so this button used to report success
        // from sessionStorage alone while the backend was still down. Probe
        // first: an unanswered /api/health keeps the banner and spends
        // nothing - not a refresh token, not the session.
        const reachable = await probeQuoteMakerHealth(undefined, RECONNECT_PROBE_TIMEOUT_MS);
        if (!reachable) {
            _setDisabled(true);
            _emitApiStatus();
            return false;
        }
        const { devAutoLogin, clearTokens, getAccessToken } = await import('./data-service-api.js');
        // Do NOT clear tokens first. `devAutoLogin` recovers in tiers, and both
        // of the tiers that can actually succeed here READ the tokens this call
        // used to destroy: tier 1 returns immediately when the existing access
        // token is unexpired (the common case — the backend blipped, the
        // session is fine), tier 2 spends the refresh token
        // (data-service-api.js:645-673). Clearing first made both unreachable,
        // so control fell through to the auto-login gate, which returns early
        // on any non-local origin — meaning on the CLOUD build this button
        // could only ever fail, and it signed the operator out on the way.
        //
        // That is the worst possible outcome for this control: the banner says
        // "changes will not save", the operator presses the one button offered,
        // and their live session is destroyed with the work still unsaved.
        await devAutoLogin();
        const tok = getAccessToken();
        if (!tok) {
            // Only now is the session genuinely unrecoverable — devAutoLogin
            // has already cleared what it proved dead. Clear defensively so a
            // half-dead token cannot linger, then report failure.
            clearTokens({ reason: 'invalid-session' });
            _emitApiStatus();
            return false;
        }
        _setDisabled(false);
        // Force a fresh API module load so the next call goes through the API path.
        _apiLoaded = false;
        _apiService = null;
        return true;
    } catch (err) {
        console.warn('[QM] Manual backend re-login failed:', err?.message);
        _emitApiStatus();
        return false;
    }
}

async function getApiService() {
    if (_apiLoaded) return _apiService;

    // Deduplicate concurrent loads: all callers share ONE promise (no busy-wait).
    // The old pattern used while(_apiLoading) + setTimeout(10ms) polling, which
    // choked the event loop when multiple DataService calls fired concurrently
    // (e.g., from React useEffects after setDocuments).
    if (_apiLoadPromise) return _apiLoadPromise;

    _apiLoadPromise = (async () => {
        try {
            const { DataServiceAPI, devAutoLogin } = await import('./data-service-api.js');
            // Timeout devAutoLogin at 5 seconds - on Windows, TCP connections to
            // non-listening ports can hang for 20-75 seconds, freezing the entire app.
            await Promise.race([
                devAutoLogin(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
            ]).catch(err => {
                console.warn('[QM] Dev auto-login skipped:', err?.message);
            });
            _apiService = DataServiceAPI;
            _apiLoaded = true;
            // v5.13.16: demoted from console.log → devLog. The backend
            // path has been the default for many releases; advertising it
            // on every reload is just noise.
            devLog('[QM] DataService: using BACKEND (PostgreSQL via API)');
        } catch (err) {
            console.warn('[QM] Backend DataService failed, falling back to Dexie:', err);
            _apiService = null;
            _apiLoaded = true;
        }
        _apiLoadPromise = null;
        return _apiService;
    })();

    return _apiLoadPromise;
}

function strictBatchProjectError(code, message, projectId, details = {}) {
    const error = new Error(message);
    error.code = code;
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = code === 'PROJECT_LOCAL_CHANGED_DURING_CLOUD_HYDRATION';
    Object.assign(error, details);
    return error;
}

/**
 * Fetch the canonical cloud Project for an unattended batch without the
 * normal read proxy's offline Dexie fallback. The server row is compatibility-
 * checked and transactionally cached before the hook may paint it.
 */
export async function loadCanonicalBackendProjectForBatch(projectId, {
    assertCompatible,
} = {}) {
    const expectedId = String(projectId || '');
    if (!expectedId) {
        throw new TypeError('loadCanonicalBackendProjectForBatch requires a Project id');
    }
    if (!USE_BACKEND) {
        throw strictBatchProjectError(
            'PROJECT_BACKEND_STRICT_READ_UNAVAILABLE',
            'Queued cloud reconciliation requires the backend API.',
            expectedId,
            { retryable: true },
        );
    }

    const api = await getApiService();
    if (!api?.projects || typeof api.projects.getById !== 'function') {
        throw strictBatchProjectError(
            'PROJECT_BACKEND_STRICT_READ_UNAVAILABLE',
            'Queued cloud reconciliation requires the backend Project API.',
            expectedId,
            { retryable: true },
        );
    }

    let serverProject;
    try {
        serverProject = await api.projects.getById(expectedId);
        _setDisabled(false);
    } catch (error) {
        if (isUnreachableError(error)) _setDisabled(true);
        throw error;
    }
    const actualServerId = String(serverProject?.id || '');
    if (!serverProject || actualServerId !== expectedId) {
        throw strictBatchProjectError(
            'PROJECT_BACKEND_IDENTITY_MISMATCH',
            `Backend returned Project ${actualServerId || '(missing id)'} while QuoteMaker requested ${expectedId}.`,
            expectedId,
            { actualProjectId: actualServerId || null, retryable: false },
        );
    }

    const localBefore = await db.projects.get(expectedId);
    const actualLocalId = String(localBefore?.id || '');
    if (!localBefore || actualLocalId !== expectedId) {
        throw strictBatchProjectError(
            'PROJECT_BACKEND_IDENTITY_MISMATCH',
            `Local cache returned Project ${actualLocalId || '(missing id)'} while QuoteMaker requested ${expectedId}.`,
            expectedId,
            { actualProjectId: actualLocalId || null, retryable: false },
        );
    }

    // The server's whole row: it versions the cached Job columns and send
    // state for the offline summary (project-summary-job-fields.ts).
    const canonical = stampCachedServerRow(_keepLocalIntakeProofOnCanonicalRead(
        localBefore,
        await mergeProjectReadResultForDexie(localBefore, serverProject),
    ), serverProject);
    assertCompatible?.(localBefore, canonical);
    const expectedLocalSignature = serverProjectContentSignature(localBefore);

    await db.transaction('rw', db.projects, async () => {
        const current = await db.projects.get(expectedId);
        if (!current
            || String(current.id || '') !== expectedId
            || serverProjectContentSignature(current) !== expectedLocalSignature) {
            throw strictBatchProjectError(
                'PROJECT_LOCAL_CHANGED_DURING_CLOUD_HYDRATION',
                `Local Project ${expectedId} changed while QuoteMaker was hydrating the canonical cloud row.`,
                expectedId,
            );
        }
        // Repeat against the transaction's current row. A concurrent local
        // builder edit cannot pass the earlier check and then be overwritten.
        assertCompatible?.(current, canonical);
        await db.projects.put(canonical);
    });

    const persisted = await db.projects.get(expectedId);
    if (!persisted
        || String(persisted.id || '') !== expectedId
        || serverProjectContentSignature(persisted) !== serverProjectContentSignature(canonical)) {
        throw strictBatchProjectError(
            'PROJECT_CANONICAL_CACHE_WRITE_FAILED',
            `Canonical cloud Project ${expectedId} was not durably cached for the queued run.`,
            expectedId,
            { retryable: true },
        );
    }
    return persisted;
}

// Mutation methods that modify data - when API succeeds, we must also
// apply the operation to Dexie so the local cache stays in sync.
// Without this, a successful API delete leaves stale data in Dexie,
// which resurfaces if the API is unavailable on next page load.
const WRITE_METHODS = new Set([
    'save', 'duplicate', 'delete', 'put', 'update', 'remove', 'deleteByProject',
    'clear', 'bulkSave', 'mergeCompanies', 'updateQuoteStatus', 'completeQuote',
    'recordPdfGenerated', 'recordPrintOpened', 'recordEmailSent',
    'recordCostingsSent', 'recordOutcome', 'recordFollowUp',
    // Wave-2 assistant persistence (Ship 2)
    'append', 'compact', 'archive', 'restore', 'createNew',
    // Assistant threads by job (spec 2026-10-07): a failed append or patch
    // must throw, never land only in the browser cache.
    'appendMessages', 'patchMessage', 'setCompaction',
    // Learning usage/outcome loop (2026-07-03). updateOutcomeForProject is
    // the cautionary tale: it existed Dexie-side but NOT in the API repo and
    // was NOT listed here, so the proxy treated it as a read and silently
    // wrote won/lost outcomes to the browser cache only — 272 Postgres
    // injection rows accumulated with outcomes permanently NULL.
    'recordUse', 'backfillUsage', 'updateOutcomeForProject', 'repoint',
    // Narrow CRM foreign-key attach (2026-08-21). Listed here so an unmapped
    // API method fails closed instead of silently writing the link to the
    // browser cache only — the updateOutcomeForProject cautionary tale above.
    'setCrmLink', 'findOrCreate', 'linkRequest',
    // Client matching on open (2026-10-08). resolveCrmLink may link the job on
    // the server, and deferCrmLink stores "Match later" there: both must throw
    // when the server does not answer, never fall back to the cache.
    'resolveCrmLink', 'deferCrmLink',
    // A job's requester links (2026-10-13): server only, so a write that does
    // not reach the server throws instead of pretending.
    'setRequesters',
    // CRM Tidy up + managing firm (2026-09-23). Writes, so an unreachable
    // backend throws instead of falling back to the offline Dexie stub (which
    // would report "offline: 0 applied" for a request that may have landed).
    'applyCleanup', 'setManager', 'setType',
]);

// v5.13.1 (WO-A, M1 fix): explicit allow-list of write method paths that
// LEGITIMATELY stay browser-local — Dexie fallback is the intended path,
// not a silent-divergence risk. Every other missing WRITE is treated as
// a code bug and throws so the caller sees the error instead of a hidden
// Dexie-only write.
//
// Format: `${store}.${method}` (qualified). Review before adding:
//   • Does this method ever need to reach Postgres? If YES, implement it
//     in data-service-api.js instead.
//   • Is this method purely UI state / cache scratch? If YES, add here.
//
// Current contents:
//   • syncState.* — browser-local cache of the server's authoritative v2
//     manifest receipt. The sync route owns the durable PostgreSQL head;
//     writing this cache through a second API route would recreate split-brain.
//
// Receivables (claude/nervous-dewdney-f86dd0, 2026-05-10): the five
// Receivables write namespaces (receivables.*, receivableEvidence.*,
// receivableProjectLinks.*, receivableActions.*, receivableSyncState.*)
// USED to live in this allow-list because Phase 1 was browser-local. They
// were removed once backend/src/routes/receivables.js + the matching
// data-service-api.js namespaces landed, so writes now flow API-first
// (with Dexie dual-write for read cache) like every other backend-mapped
// resource. See docs/plans/receivables-postgres-backend-handoff-2026-05-10.md.
//
// Kill switch: `qm_disable_fail_closed_writes` — when set to 'true' in
// localStorage, the proxy reverts to the pre-v5.13.1 silent-fallback
// behaviour for ALL missing writes. Pilot-day safety hatch only.
const LOCAL_ONLY_METHODS = new Set([
    'syncState.save', 'syncState.delete',
    'syncState.deleteByProject',
    'syncState.clear',
    // Tasks page slice 1 (Dexie v31, SPEC-tasks-page.md §11) — browser-local
    // until the server floor lands. PROMOTED IN SLICE 5: remove ALL seven
    // entries in the SAME COMMIT that adds the data-service-api.js tasks
    // namespace, add 'setStatus' to WRITE_METHODS (the updateOutcomeForProject
    // cautionary tale above — a write not listed there silently degrades to a
    // browser-only mutation), and add the `key === 'tasks'` mirror branch that
    // mirrors the CANONICAL server row, not the request args (DEC-PE-010 class).
    'tasks.save', 'tasks.bulkSave', 'tasks.setStatus',
    'tasks.getAll', 'tasks.getOpen', 'tasks.getByProject', 'tasks.getBySourceKeys',
]);

function _failClosedKillSwitchOn() {
    try {
        return typeof localStorage !== 'undefined'
            && localStorage.getItem('qm_disable_fail_closed_writes') === 'true';
    } catch { return false; }
}

/**
 * The local row a `projects.save` result is mirrored onto.
 *
 * The mirror is built as {...request, ...server}, and device-local locators
 * (blobId / docBlobId / videoBlobId) are then restored from the REQUEST's
 * lanes only. That is right for a full save, where the request carries every
 * lane. It is wrong for a partial patch: the load-time cloud photo repair
 * saves just { id, photos }, so the videos and documents lanes came back from
 * the server locator-free, the merged row carried the server's `title`, and the
 * local save therefore treated it as a full record and REPLACED the row. Every
 * freshly imported project lost videos[].videoBlobId, documents[].docBlobId
 * and its local-only fields (backendVerified*, intakeSourcePath) seconds into
 * its first Quote Run. The video bytes were still in video_blobs; only the
 * pointer was gone, which surfaced as PROJECT_VIDEO_SOURCE_UNAVAILABLE.
 *
 * For a partial patch, mirror onto the existing local row overlaid with the
 * patch. Locators are still restored only after the same ownership proof. A
 * full save is unchanged, so a full save can still drop a key by omitting it.
 * `expectedUpdatedAt` is a request-only concurrency token and never belongs
 * in the stored row, where a later save could read it back as current.
 */
async function _projectSaveMirrorBase(request) {
    if (!request || typeof request !== 'object' || !request.id) return request;
    const looksPartial = !('title' in request)
        && !('formData' in request)
        && !('scope' in request)
        && !('currentStage' in request);
    if (!looksPartial) return request;
    let localBefore = null;
    try {
        localBefore = await db.projects.get(request.id);
    } catch {
        localBefore = null;
    }
    if (!localBefore) return request;
    const { expectedUpdatedAt: _requestOnlyToken, ...patch } = request;
    const { expectedUpdatedAt: _staleStoredToken, ...localRow } = localBefore;
    return { ...localRow, ...patch };
}

// The local statuses the API save collapses to 'draft' on the wire
// (data-service-api.js LOCAL_PROJECT_STATUS_TO_BACKEND). They exist only in this
// browser: the server has no such value to send back.
const _LOCAL_INTAKE_WORKFLOW_STATUSES = new Set([
    'pre-processed', 'preprocessed', 'ready_for_analysis', 'ready-for-analysis', 'intake_complete',
]);

// The four fields hasDurableIntakeReadyProof reads besides the status. They are
// written to Dexie only (queue-pre-process-enqueue.js) and no server column
// carries them, so the server has no opinion here to overrule.
const _LOCAL_INTAKE_PROOF_FIELDS = [
    'backendVerificationMode', 'backendVerifiedAt', 'backendVerifiedProjectId', 'backendVerifiedUpdatedAt',
];

/**
 * The canonical cloud read must not void the intake-ready proof either.
 *
 * mergeProjectReadResultForDexie rebuilds the row from the server copy and
 * merges back only media locators, so every local-only field is dropped and
 * the wire's 'draft' replaces the local intake status. That row is then written
 * to Dexie as canonical.
 *
 * Live, 2026-09-20, the first 16-project cloud run: three projects reached
 * their quote job with status 'draft' and all four proof fields absent, hours
 * after their intakes had completed, and failed in 1 to 5 seconds as
 * PROJECT_LOCAL_CHANGED_DURING_BACKEND_RECONCILIATION,
 * PROJECT_BACKEND_NEWER_THAN_LOCAL and PROJECT_BACKEND_SYNC_UNVERIFIED. Each
 * local row had been rewritten within seconds of its job starting.
 *
 * Same narrow shape as _keepLocalIntakeWorkflowStatus below: the status is kept
 * only when the server's answer is exactly the wire's translation of it, so a
 * real server-side status still wins.
 */
function _keepLocalIntakeProofOnCanonicalRead(localBefore, canonical) {
    if (!canonical || typeof canonical !== 'object' || !localBefore || typeof localBefore !== 'object') return canonical;
    const keepStatus = _LOCAL_INTAKE_WORKFLOW_STATUSES.has(localBefore.status) && canonical.status === 'draft';
    const restored = _LOCAL_INTAKE_PROOF_FIELDS
        .filter(field => localBefore[field] !== undefined && canonical[field] === undefined);
    if (!keepStatus && restored.length === 0) return canonical;
    const next = { ...canonical };
    if (keepStatus) next.status = localBefore.status;
    for (const field of restored) next[field] = localBefore[field];
    return next;
}

/**
 * A partial patch must not void the intake-ready proof.
 *
 * The proof (hasDurableIntakeReadyProof) needs status 'ready_for_analysis' as
 * well as the backendVerified* fields the mirror now keeps. The server answers
 * every save with 'draft' for such a project, and the merge lets every server
 * field win, so opening a freshly imported project (its load-time photo repair
 * saves just { id, photos }) rewrote the local status to 'draft'. The next
 * Import Projects mount then found the proof missing, recorded
 * INTAKE_READY_PROOF_MISSING (a code in the production record) and queued the
 * intake again over a project that had already completed it.
 *
 * Narrow on purpose: only a partial patch that did not set status itself, only
 * a local intake workflow status, and only when the server's answer is exactly
 * the wire's translation of it. A real server-side status still wins.
 */
function _keepLocalIntakeWorkflowStatus(request, base, merged) {
    if (base === request || !merged || typeof merged !== 'object') return merged;
    if (request && Object.prototype.hasOwnProperty.call(request, 'status')) return merged;
    const localStatus = base?.status;
    if (!_LOCAL_INTAKE_WORKFLOW_STATUSES.has(localStatus) || merged.status !== 'draft') return merged;
    return { ...merged, status: localStatus };
}

// v5.12.0 (2026-04-22): outbox, permanent-error classifier, and 3-strike
// kill switch were ALL removed. The previous design queued failed writes
// with Dexie-generated string IDs that Postgres could never ingest (FK +
// UUID-syntax violations), then silently dropped them: the classic lossy
// dual-source pattern. Replacement is simpler: writes are API-only, and if
// the backend can't accept the write the caller sees a thrown error and
// the UI shows a reconnection banner. No queue, no replay, no drop.
//
// One-time cleanup: if a legacy install has rows in the old
// `pending_writes` table, we empty it on first successful backend op so
// the table doesn't hold Dexie IDs that will never sync.
let _legacyOutboxCleared = false;
async function _clearLegacyOutbox() {
    if (_legacyOutboxCleared) return;
    _legacyOutboxCleared = true;
    try {
        if (db?.pending_writes && typeof db.pending_writes.clear === 'function') {
            const count = await db.pending_writes.count().catch(() => 0);
            if (count > 0) {
                await db.pending_writes.clear();
                console.log(`[DataService] Cleared ${count} legacy pending_writes row(s): outbox retired in v5.12.0`);
            }
        }
    } catch { /* non-fatal */ }
}

// Create a proxy that delegates to either Local (Dexie) or API service.
//
// Contract (v5.12.0):
//   • WRITE (method ∈ WRITE_METHODS):
//       API call first. On success → dual-write to Dexie (read cache).
//       On unreachable-class failure → flip `disabled = true`, THROW to caller.
//       The caller's try/catch (or uncaught promise handler) surfaces a user-
//       visible error. We do NOT silently write to Dexie on failure.
//       On data-shape failure (400/404/422, etc.) → just throw. Don't flip
// `disabled`. That's a code/data bug, not a connectivity problem.
//   • READ:
//       API call first. On unreachable-class failure → fall back to Dexie
//       cache (stale-while-revalidate). Flip `disabled = true` so the banner
//       shows. On 404/missing → propagate normally (that IS the answer).
function createProxy(localService) {
    if (!USE_BACKEND) return localService;

    const proxy = {};
    for (const [key, value] of Object.entries(localService)) {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            proxy[key] = {};
            for (const [method, rawFn] of Object.entries(value)) {
                if (typeof rawFn === 'function') {
                    // v5.16.9 hot-fix (Peter 2026-05-04 — caught via console
                    // error chain on failed device): destructuring rawFn from
                    // Object.entries(value) loses the `this` binding. Methods
                    // like companies.mergeCompanies that internally call
                    // `this.previewMerge` then crash with "Cannot read
                    // properties of undefined (reading 'previewMerge')".
                    // The Dexie cache refresh swallows the error as
                    // "non-fatal", but the local cache is now stale: the
                    // merged-away company_id stays in cached project rows,
                    // and the next project save FK-violates against the
                    // backend (companies row is gone server-side).
                    // Fix: bind to the parent service object so `this` works.
                    const fn = rawFn.bind(value);
                    const isWrite = WRITE_METHODS.has(method);
                    proxy[key][method] = async (...args) => {
                        const strictReadOnly = !isWrite
                            && key === 'businessPricing'
                            && method === 'get'
                            && args[0]?.strictReadOnly === true;
                        const api = await getApiService();
                        if (!api || !api[key] || typeof api[key][method] !== 'function') {
                            if (strictReadOnly) {
                                throw new Error(
                                    '[DataService] Strict read-only business-pricing retrieval requires the backend API.',
                                );
                            }
                            // v5.13.1 (WO-A, M1): fail-closed on unmapped
                            // WRITEs unless the method is either on the
                            // LOCAL_ONLY_METHODS allow-list OR the kill
                            // switch `qm_disable_fail_closed_writes` is set.
                            // Reads still fall back silently (offline UX).
                            if (isWrite) {
                                const qualified = `${key}.${method}`;
                                const allowLocal = LOCAL_ONLY_METHODS.has(qualified);
                                const killSwitchOn = _failClosedKillSwitchOn();
                                if (!allowLocal && !killSwitchOn) {
                                    throw new Error(
                                        `[DataService] Write '${qualified}' has no backend API method. `
                                        + `This is a code bug — either implement it in data-service-api.js `
                                        + `or add '${qualified}' to LOCAL_ONLY_METHODS if it is intentionally browser-local.`
                                    );
                                }
                            }
                            // READ path OR explicitly-local WRITE: fall
                            // back to Dexie for these by design.
                            return fn(...args);
                        }

                        try {
                            // strictReadOnly is a local proxy contract, not an
                            // API query/body field. Do not forward it.
                            const result = await api[key][method](...(strictReadOnly ? [] : args));
                            // Purge any legacy outbox rows left behind by prior
                            // versions. Every evaluator read must be entirely
                            // maintenance-free: even detached cleanup can race into
                            // the mutation guard after this read resolves. Writes
                            // remain governed by the normal mutation guard and fail
                            // closed at their boundary.
                            //
                            // This does NOT mark the server as answering. A call
                            // resolving proves nothing: drafts.get,
                            // aiConversations.getCurrent, tenantProfile.get and
                            // bios.business.get end in `.catch(() => null)`, so
                            // during the 2026-09-29 deploy an Assistant panel
                            // mount resolved null from a 502, took the outage
                            // banner down, drained the held writes into the 502
                            // and reopened the project save gate (outage lane
                            // review). apiFetch marks the server answering on a
                            // real 2xx, and the reconnect loop on a healthy
                            // /api/health; nothing else may.
                            const suppressReadMaintenance = !isWrite
                                && isEvalAiSingleAttemptPolicyActive();
                            if (!strictReadOnly && !suppressReadMaintenance) {
                                _clearLegacyOutbox().catch(() => {});
                            }
                            // The offline summary needs the Job columns only this
                            // read carries (the quote_jobs join fields, and a
                            // revision the server made since this device last
                            // saved the quote). Detached, so the read never waits
                            // on the cache.
                            if (!isWrite && !suppressReadMaintenance
                                && key === 'projects' && method === 'getAllSummaries'
                                && Array.isArray(result)) {
                                _rememberSummaryJobFields(result).catch((cacheErr) => {
                                    console.warn('[QM] Could not keep the Job columns for the offline summary (non-fatal):', cacheErr?.message || cacheErr);
                                });
                            }
                            // Dual-write Dexie so reads next time don't have to
                            // round-trip. Fire-and-forget: cache failure is OK.
                            if (isWrite) {
                                try {
                                    // DEC-PE-010: for businessPricing.save the backend may
                                    // TRANSFORM the payload — the atomic guard preserves a
                                    // populated catalogue array against an empty-overwrite — so
                                    // the request body can differ from what actually persisted.
                                    // Mirror the CANONICAL backend result to Dexie, NOT the
                                    // incoming args, so an offline / cache-first read can't
                                    // surface a stale-empty (poisoned) local snapshot. Normalise
                                    // the key to 'default' (the local row key; the backend row
                                    // id differs). Other writes mirror their args as before.
                                    if (key === 'companies' && method === 'mergeCompanies') {
                                        await fn(args[0], args[1], result);
                                    } else if (key === 'companies' && method === 'findOrCreate') {
                                        await fn(args[0], result);
                                    } else if (key === 'companies' && method === 'applyCleanup') {
                                        // Mirror the rows the server changed, read off its receipt.
                                        await fn(args[0], result);
                                    } else if (key === 'companies' && method === 'setManager') {
                                        await fn(args[0], args[1] ?? null, result);
                                    } else if (key === 'companies' && method === 'setType') {
                                        // Mirror the row PostgreSQL returned.
                                        await fn(args[0], args[1], result);
                                    } else if (key === 'companies' && method === 'save' && result && typeof result === 'object') {
                                        // The body may be partial ({ id, notes }); mirror the
                                        // row PostgreSQL returned, never the request.
                                        await fn(args[0], result);
                                    } else if (key === 'businessPricing' && method === 'save' && result && typeof result === 'object') {
                                        await fn({ ...result, id: 'default' });
                                    } else if (key === 'goldStandards' && method === 'save' && result && typeof result === 'object') {
                                        // The multipart backend path may create the canonical
                                        // UUID and durable PDF pointer atomically. Mirror only
                                        // canonical metadata: retaining a cloud-uploaded PDF Blob
                                        // in Dexie can consume up to the transport envelope again,
                                        // exhaust browser quota, and stall a long-lived tab. Cloud
                                        // previews use the same-origin range-stream route instead.
                                        const {
                                            pdfBlob: _uploadedPdfBlob,
                                            pdfFile: _uploadedPdfFile,
                                            ...localMetadata
                                        } = args[0] || {};
                                        await fn({
                                            ...localMetadata,
                                            ...result,
                                        });
                                    } else if (key === 'projects' && method === 'save' && result && typeof result === 'object') {
                                        // A full save can race a narrow lifecycle or Stage-5
                                        // completion write. The backend merges those server-owned
                                        // receipts into its canonical response; mirroring the
                                        // original request here would immediately roll Dexie back
                                        // to the stale tab snapshot even though PostgreSQL is safe.
                                        // Keep request-only browser fields, but let every persisted
                                        // server field win. The result is the whole server row, so
                                        // it versions the cached Job columns and send state for the
                                        // offline summary (project-summary-job-fields.ts).
                                        const mirrorBase = await _projectSaveMirrorBase(args[0]);
                                        await fn(stampCachedServerRow(_keepLocalIntakeWorkflowStatus(
                                            args[0],
                                            mirrorBase,
                                            await mergeProjectSaveResultForDexieOwned(mirrorBase, result),
                                        ), result));
                                    } else if (key === 'projects' && method === 'duplicate' && result && typeof result === 'object') {
                                        await fn(args[0], args[1], result);
                                    } else if (key === 'projects' && method === 'completeQuote' && result && typeof result === 'object') {
                                        await fn(args[0], { ...(args[1] || {}), serverResult: result });
                                    } else if (key === 'projects' && method === 'recordPdfGenerated' && result && typeof result === 'object') {
                                        await fn(args[0], {
                                            ...(args[1] || {}),
                                            pdfGeneratedAt: result.pdfGeneratedAt || result.generatedAt,
                                            updatedAt: result.updatedAt,
                                            aiContext: result.aiContext,
                                        });
                                    } else if (key === 'projects' && method === 'recordPrintOpened' && result && typeof result === 'object') {
                                        await fn(args[0], {
                                            ...(args[1] || {}),
                                            printOpenedAt: result.printOpenedAt,
                                            updatedAt: result.updatedAt,
                                            aiContext: result.aiContext,
                                        });
                                    } else if (key === 'projects' && method === 'recordEmailSent' && result && typeof result === 'object') {
                                        await fn(args[0], {
                                            ...(args[1] || {}),
                                            emailSentAt: result.emailSentAt,
                                            updatedAt: result.updatedAt,
                                            aiContext: result.aiContext,
                                        });
                                    } else if (key === 'projects' && method === 'recordCostingsSent' && result && typeof result === 'object') {
                                        await fn(args[0], {
                                            ...(args[1] || {}),
                                            costingsSentAt: result.costingsSentAt,
                                            updatedAt: result.updatedAt,
                                            aiContext: result.aiContext,
                                        });
                                    } else if (key === 'projects' && method === 'recordOutcome' && result && typeof result === 'object') {
                                        await fn(args[0], {
                                            ...(args[1] || {}),
                                            status: result.status || args[1]?.status,
                                            outcomeAt: result.outcomeAt,
                                            updatedAt: result.updatedAt,
                                            aiContext: result.aiContext,
                                        });
                                    } else if (key === 'projects' && method === 'updateQuoteStatus') {
                                        // Hand the server's updatedAt on as the receipt time the
                                        // offline summary compares with the last online list.
                                        await fn(args[0], args[1], {
                                            updatedAt: result && typeof result === 'object' ? (result.updatedAt ?? null) : null,
                                        });
                                    } else if (key === 'projects' && method === 'recordFollowUp' && result && typeof result === 'object') {
                                        await fn(args[0], {
                                            ...(args[1] || {}),
                                            updatedAt: result.updatedAt,
                                            aiContext: result.aiContext,
                                        });
                                    } else if (key === 'projects' && method === 'setCrmLink' && result && typeof result === 'object') {
                                        // Mirror the SERVER's updatedAt. This is a
                                        // bookkeeping write, so the value comes back
                                        // unchanged by design (migration 102) and the
                                        // cached optimistic-concurrency token stays
                                        // valid for the tab's next full save.
                                        await fn(args[0], {
                                            companyId: result.companyId ?? result.company_id ?? null,
                                            propertyId: result.propertyId ?? result.property_id ?? null,
                                            updatedAt: result.updatedAt,
                                        });
                                    } else if (key === 'projects' && (method === 'resolveCrmLink' || method === 'deferCrmLink')) {
                                        // Mirror what the SERVER answered (a link it made or
                                        // found, the deferral it stored), never the request.
                                        await fn(args[0], args[1], result);
                                    } else {
                                        await fn(...args);
                                    }
                                } catch (dexieErr) {
                                    // Stage 5 completion is verified immediately from Dexie by
                                    // PipelineRunner. Swallowing this cache write would report
                                    // success while the batch remains non-terminal locally.
                                    if (key === 'projects' && method === 'completeQuote') {
                                        dexieErr.qmApiWriteSucceeded = true;
                                        throw dexieErr;
                                    }
                                    console.warn(`[QM] Dexie cache refresh for ${key}.${method} failed (non-fatal):`, dexieErr?.message);
                                }
                            }
                            if (key === 'projects' && method === 'getById' && result && typeof result === 'object') {
                                let localProject = null;
                                try {
                                    localProject = await fn(args[0]);
                                } catch (dexieErr) {
                                    console.warn(
                                        '[QM] Local Project cache unavailable during owner-validated media recovery; using portable cloud media only:',
                                        dexieErr?.message || dexieErr,
                                    );
                                }
                                return mergeProjectReadResultForDexie(localProject, result);
                            }
                            return result;
                        } catch (err) {
                            if (err?.qmApiWriteSucceeded) {
                                console.warn(
                                    `[QM] API ${key}.${method} succeeded, but local completion verification failed:`,
                                    err?.message || err,
                                );
                                throw err;
                            }
                            const unreachable = isUnreachableError(err);

                            if (isWrite) {
                                // Writes must not silently diverge from Postgres.
                                // Flip the banner if the backend looks unreachable,
                                // then rethrow so the caller can surface the error.
                                if (unreachable) _setDisabled(true);
                                console.warn(`[QM] API ${key}.${method} failed (${unreachable ? 'unreachable' : 'error'}):`, err?.message || err);
                                throw err;
                            }

                            // READ path.
                            if (strictReadOnly) throw err;
                            if (unreachable) {
                                _setDisabled(true);
                                // Serve stale Dexie cache rather than a hard error.
                                // the user sees last-known-good data + the banner.
                                return fn(...args);
                            }
                            // 404 / 400 / 422 → let the caller distinguish.
                            throw err;
                        }
                    };
                } else {
                    proxy[key][method] = rawFn;
                }
            }
        } else if (typeof value === 'function') {
            // DATA-11: removed duplicate `else if (typeof value === 'function')`
            //. The second copy was dead code (first match always wins) and
            // made the intent ambiguous.
            // Top-level function
            proxy[key] = value;
        } else {
            proxy[key] = value;
        }
    }
    return proxy;
}

const DataService = createProxy(DataServiceLocal);

export { DataService };
